// src/contexts/GameFlowProvider.jsx
// Performance-optimized with Supabase Broadcast for instant sync
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { supabase } from '@tasted/utils/supabase';
import { reinitForSessionType, setSessionContext, track } from '@tasted/utils/analytics';
import { netTier, setSessionTierCap, startObserver, probeConnection, NET_MIDDLE, NET_SLOW } from '@tasted/utils/netTier';
import { warmBottleFor, warmRevealAssets } from '../lib/warmRevealAssets';
import { useSearchParams } from 'react-router-dom';
import { normalizeCorrectAnswer } from '../utils/normalizeCorrectAnswer';
import { shuffleQuestionOptions } from '../utils/shuffleOptions';
import { playerStorage, gmSecretStorage, claimTokenStorage } from '../utils/playerStorage';
import { FORK_CHOICE_KEY } from '../components/WelcomeFork';
import { resilientChannel, isLiveStateReconcileEnabled, isSyncGatingEnabled, shouldSendSyncRequest, jitterDelay, isTvModeEnabled } from '../utils/realtime';
import { createBroadcastReconciler } from '../utils/broadcastGuard';
import { mergeQuestionFeedback } from '../utils/scoreReceipt';
import { decideLiveStateApply, liveRowRound } from '../utils/liveState';
import { readLiveStateFresh, registerReconciler } from '../utils/sessionRecovery';
import { requestQueue, entryTag } from '../utils/requestQueue';
import { gameEventOp, appendGameEvent } from '../utils/gameEvents';
import {
  fetchRoomFeatures,
  ROOM_FEATURES_OFF,
  ROOM_FEATURES_REFRESH_MS,
  sameRoomFeatures,
} from '../utils/sessionFeatures';
import { GameFlowRegistryContext } from './gameFlowRegistry';
import { toast } from 'sonner';
import { useCatchUpOnRecovery } from '../hooks/useCatchUpOnRecovery';
import { isQuestionRowMissing, reportMissingQuestionOnce } from '../lib/missingQuestion';
import { roundBottleForStep } from '../lib/roundBottles';

// Suppress verbose debug logging in production to reduce performance overhead
const __DEV__ = import.meta.env.DEV;
const debugLog = __DEV__ ? console.log.bind(console) : () => {};
const debugWarn = __DEV__ ? console.warn.bind(console) : () => {};
const debugGroup = __DEV__ ? (console.groupCollapsed || console.group || (() => {})).bind(console) : () => {};
const debugGroupEnd = __DEV__ ? (console.groupEnd || (() => {})).bind(console) : () => {};

// TAS-171: unique gm-next-fallback channel topics (see the connect() comment).
let gmNextFallbackSeq = 0;
// TAS-178: unique session_live_state channel topics (postgres_changes need a
// fresh name per (re)subscribe attempt — same rule as the gm-next fallback).
let liveStateChannelSeq = 0;


// TAS-190: claim the GM advance token and cache it per session. `force` re-claims
// even when one is cached — used to recover when a GM handoff rotated the secret
// out from under a re-promoted GM whose device still holds the old one (migration
// 059 lets the current GM re-bind). Best-effort; returns the secret or null.
async function claimAndStoreGmSecret(sessionCode, playerId, { force = false } = {}) {
  if (!sessionCode || !playerId) return null;
  const cached = gmSecretStorage.get(sessionCode);
  if (!force && cached) return cached;
  try {
    const { data: secret, error } = await supabase.rpc('claim_gm_secret_secure', {
      p_session_id: sessionCode,
      p_player_id: playerId,
      p_claim_token: claimTokenStorage.get(playerId),
    });
    if (!error && secret) {
      gmSecretStorage.set(sessionCode, secret);
      return secret;
    }
  } catch { /* best-effort — advance falls back / retries */ }
  return null;
}

// TAS-374: the locally cached step for a session, or 0 when there is none.
//
// Written by this device on every advance, so on a mid-game reload it is almost
// always the room's step — which makes it a good SEED, and a terrible AUTHORITY.
// It lives in client-writable storage, so a player could write a later index and
// jump their own client to a reveal the table has not reached (TAS-25). It is
// therefore only ever provisional: the first authoritative answer replaces it,
// in either direction, and until that answer lands the UI shows a recovery state
// rather than this value.
function readCachedStep(code) {
  if (!code) return 0;
  try {
    const fromSession = sessionStorage.getItem(`tasted_step_${code}`);
    const fromLocal = localStorage.getItem(`tasted_step_${code}`);
    const best = Math.max(
      fromSession !== null ? Number(fromSession) : -1,
      fromLocal !== null ? Number(fromLocal) : -1
    );
    return Number.isFinite(best) && best > 0 ? best : 0;
  } catch {
    return 0; // storage unavailable
  }
}

// TAS-415 review: pulled out of the combined loader so both the initial fetch
// and a leg-scoped retry build question objects the same way, without a
// second copy of this transform drifting from the first.
export function enrichQuestions(rows) {
  return rows.map((q) => {
    const sessionOptions = Array.isArray(q.options) ? q.options : null;
    const masterOptions = q.questions_v2?.options ?? [];
    const selectionMode = q.questions_v2?.selection_mode ?? null;
    const isGroupQuestion = selectionMode === 'group';
    // Grouped questions display joined-string groups (e.g. "Apple, Pear, Citrus")
    // synthesised at session-create time. Falling back to master options here
    // would surface the raw components, which never match the joined correct
    // answer — so for group questions, no session subset means no options.
    let resolvedOptions;
    if (sessionOptions && sessionOptions.length > 0) {
      resolvedOptions = sessionOptions;
    } else if (isGroupQuestion) {
      console.warn(
        '[GameFlow] Group question missing session options subset',
        { sessionQuestionId: q.id, questionId: q.question_id }
      );
      resolvedOptions = [];
    } else {
      resolvedOptions = masterOptions;
    }
    // Coerce to a JS array regardless of column storage (text / text[] / jsonb)
    // so downstream consumers (RevealAnswers, Results, scoring) don't have to.
    let coercedCorrect = normalizeCorrectAnswer(q.correct_answer);
    // Group-mode answers are stored as raw components in some legacy code
    // paths but the displayed option is the joined string. Detect this by
    // comparing against the displayed options — if no component matches
    // the displayed options but a join would, use the join. (Idempotent
    // when correct_answer is already the joined form.)
    if (isGroupQuestion && coercedCorrect.length > 0) {
      const optionSet = new Set(resolvedOptions || []);
      const alreadyJoined = coercedCorrect.some((v) => optionSet.has(String(v)));
      if (!alreadyJoined) {
        const joined = coercedCorrect
          .map((s) => String(s).trim())
          .filter(Boolean)
          .join(', ');
        if (joined) coercedCorrect = [joined];
      }
    }
    return {
      ...q,
      text: q.questions_v2?.text ?? '',
      type: q.questions_v2?.type ?? 'single-select',
      selection_mode: selectionMode,
      // Fairness: shuffle deterministically by the session-question id so the
      // correct answer isn't rendered top-left (~80% of questions store it as
      // options[0]). Stable per question + same for all clients; scoring/50-50
      // are value-based so order doesn't matter to them.
      options: shuffleQuestionOptions(resolvedOptions, q.id ?? q.question_id),
      correct_answer: coercedCorrect,
      explanation: q.questions_v2?.explanation ?? '',
      metadata: q.questions_v2?.metadata ?? {},
      hint: q.questions_v2?.hint ?? '',
      hintImage: q.questions_v2?.hint_image || null,
      tipCorrect: q.questions_v2?.tip_correct ?? '',
      tipWrong: q.questions_v2?.tip_wrong ?? '',
      tipCloseCall: q.questions_v2?.tip_close_call ?? '',
      closeCallOptions: q.questions_v2?.close_call_options ?? [],
    };
  });
}

// TAS-415 review (blocker): a leg-scoped retry that only failed on `wines`
// still had to re-fetch `game_questions` in the old bundled-retry code, and a
// slow one of those could resolve AFTER the reveal's own prefetch merge
// (mergeQuestionFeedback, above) had already unlocked correct_answer/feedback
// into `wineQuestions` — replacing the array wholesale erased that feedback.
// Per-leg retries below no longer re-fetch a leg that did not fail, which
// removes the trigger, but this merge is the backstop: an incoming row never
// overwrites feedback fields the existing entry already unlocked, and any id
// not yet present (first successful load, or a genuinely new question) is
// added as-is.
export function mergeQuestionsPreservingFeedback(prev, fresh) {
  const byId = new Map(prev.map((q) => [q.id, q]));
  return fresh.map((q) => {
    const existing = byId.get(q.id);
    if (!existing) return q;
    const existingHasFeedback = Array.isArray(existing.correct_answer) && existing.correct_answer.length > 0;
    const freshIsRedacted = !Array.isArray(q.correct_answer) || q.correct_answer.length === 0;
    if (existingHasFeedback && freshIsRedacted) {
      return {
        ...q,
        correct_answer: existing.correct_answer,
        explanation: existing.explanation,
        tipCorrect: existing.tipCorrect,
        tipWrong: existing.tipWrong,
        tipCloseCall: existing.tipCloseCall,
      };
    }
    return q;
  });
}

// TAS-415 review (note, widened in round 3): a cheap signature so a retry
// that confirms what we already had skips the setState entirely —
// wineQuestions is a dependency of downstream effects (Game.jsx's asset
// prefetch among them), and a same-content replace was restarting a 5s image
// load for nothing. Round 3: the first cut only covered id/correct_answer/
// explanation, so a legitimately changed options list, question text, hint
// or tip (an admin editing the catalogue mid-session, however rare) would
// have been silently skipped as "unchanged" — options/text/hint/every tip
// are now all part of the signature too.
export function questionsFeedbackSignature(list) {
  return list
    .map((q) => [
      q.id,
      (q.correct_answer || []).join(','),
      q.explanation || '',
      q.tipCorrect || '',
      q.tipWrong || '',
      q.tipCloseCall || '',
      (q.options || []).join(','),
      q.text || '',
      q.hint || '',
    ].join('|'))
    .join(';');
}

export const GameFlowContext = createContext(null);
export const useGameFlow = () => {
  const context = useContext(GameFlowContext);
  if (context === null) {
    throw new Error('useGameFlow must be used within a GameFlowProvider');
  }
  return context;
};

/**
 * Part 2 (H5): run `cb({ roundId, attempt, stepIndex })` when the host restarts
 * a question. Pass the round id the screen is showing to hear only that round,
 * or null to hear every restart. Safe outside a GameFlowProvider (no-op). The
 * latest `cb` is always the one called, so it need not be memoised.
 */
export function useRoundRestart(roundId, cb) {
  const ctx = useContext(GameFlowContext);
  const cbRef = useRef(cb);
  useEffect(() => { cbRef.current = cb; });
  const subscribe = ctx?.subscribeRoundRestart;
  useEffect(() => {
    if (typeof subscribe !== 'function') return undefined;
    return subscribe((evt) => {
      if (roundId && evt?.roundId !== roundId) return;
      try { cbRef.current?.(evt); } catch (e) { debugWarn('[GF] round-restart listener failed:', e); }
    });
  }, [subscribe, roundId]);
}

// The copy shown when advance_game_step fails for a reason the server did not
// put into words (network, timeout). A server P04xx/42501 message is shown as is.
const ADVANCE_FAILED_COPY = "Couldn't move everyone on — check your connection and try again.";

// TAS-274: `displayMode` mounts the provider for the standalone /tv big screen —
// a player-less follower. It never advances the room (it has no currentPlayerId,
// so the GM checks/advance already no-op), and its stepIndex is driven purely by
// the broadcast + session_live_state reconcile paths that every follower uses.
export function GameFlowProvider({ children, displayMode = false }) {
  const [searchParams] = useSearchParams();
  const [sessionCode, setSessionCode] = useState(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      debugLog(
        `📦 GameFlowProvider: Initializing sessionCode from URL: ${codeFromUrl}`
      );
    } else {
      debugWarn(
        '📦 GameFlowProvider: sessionCode not found in URL during initialization.'
      );
    }
    return codeFromUrl;
  });

  // Track previous sessionCode to avoid clearing on initial mount
  const prevSessionCodeRef = useRef(sessionCode);

  const [gameType, setGameType] = useState(null);
  const [gameMode, setGameMode] = useState('party');
  const [sessionType, setSessionType] = useState(null); // 'test' | 'prod' — loaded from DB
  const [features, setFeatures] = useState({});

  // Part 2 (spec §1 211, M1): the per-session room flags. All off until the
  // server says otherwise, so a production game (flags off) and a client whose
  // read failed both stay on the legacy paths. `roomFeaturesReady` gates the
  // hydration paths that choose between the live row and the legacy gm-next
  // max, so they choose once, with the answer in hand.
  const [roomFeatures, setRoomFeatures] = useState(ROOM_FEATURES_OFF);
  const [roomFeaturesReady, setRoomFeaturesReady] = useState(false);
  const roomFeaturesRef = useRef(ROOM_FEATURES_OFF);
  const stepAuthorityRef = useRef(false);
  const refreshRoomFeaturesRef = useRef(null);
  useEffect(() => {
    roomFeaturesRef.current = ROOM_FEATURES_OFF;
    stepAuthorityRef.current = false;
    setRoomFeatures(ROOM_FEATURES_OFF);
    if (!sessionCode) {
      setRoomFeaturesReady(true);
      refreshRoomFeaturesRef.current = null;
      return undefined;
    }
    setRoomFeaturesReady(false);
    let disposed = false;
    let seq = 0;
    let everOk = false;
    const refresh = async () => {
      const mine = ++seq;
      const { features: next, ok } = await fetchRoomFeatures(sessionCode);
      if (disposed || mine !== seq) return;
      // A failed refresh keeps what we had: a network blip mid-game must not
      // flip a room between paths. Only the first answer may be a failure.
      if (ok || !everOk) {
        if (ok) everOk = true;
        if (!sameRoomFeatures(roomFeaturesRef.current, next)) {
          roomFeaturesRef.current = next;
          stepAuthorityRef.current = Boolean(next.room_step_authority);
          setRoomFeatures(next);
        }
      }
      setRoomFeaturesReady(true);
    };
    refreshRoomFeaturesRef.current = refresh;
    refresh();
    // A read that hangs must not hold hydration: after 3s decide with the
    // default (legacy) and let a later refresh switch the room over.
    const readyFailsafe = setTimeout(() => { if (!disposed) setRoomFeaturesReady(true); }, 3000);
    const interval = setInterval(() => { refresh(); }, ROOM_FEATURES_REFRESH_MS);
    const onFocus = () => refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      refreshRoomFeaturesRef.current = null;
      clearTimeout(readyFailsafe);
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [sessionCode]);
  const refreshRoomFeatures = useCallback(() => refreshRoomFeaturesRef.current?.(), []);

  // Start watching what this connection actually manages as early as the game
  // has a provider, so the first wine reveal is a decision rather than a guess
  // (TAS-398). The probe covers the case where watching learns nothing: the
  // heavy assets are invisible to resource timing, and a returning player's
  // bundle comes off the disk cache. It costs ~200KB, once, and only if no
  // real traffic has said anything by then.
  useEffect(() => {
    startObserver();
    const t = setTimeout(() => { probeConnection(); }, 2000);
    return () => clearTimeout(t);
  }, []);
  // TAS-274: 'phone' (default) | 'tv'. Chosen by the GM on the Game View Mode
  // step and stored as a `game-view-mode-set` game event. Forced to 'phone'
  // whenever TV mode is off, so the whole feature is inert.
  const [gameViewMode, setGameViewMode] = useState('phone');
  const [sessionPrizes, setSessionPrizes] = useState(null);
  const [sessionDataLoaded, setSessionDataLoaded] = useState(false);
  const [flowSteps, setFlowSteps] = useState([]);
  // TAS-374: still 0, and deliberately so — but nothing RENDERS it until
  // `isHydrating` clears, because 0 is a placeholder and not a claim. A mid-game
  // mount that paints step 0 shows wine 1's question to a room that is on wine
  // 2, and a blank screen to any player whose data for that step has not loaded.
  // Nine players hit exactly that in prod session 5017 (37 remounts, every one
  // reported as fromStep 0).
  //
  // The `tasted_step_<code>` cache is used to WARM the likely step's assets
  // during hydration (see the prefetch below) but never to decide what is on
  // screen. It is client-writable, so letting it pick the step would hand a
  // player a way to jump their own client to a reveal the table has not reached
  // — the hole TAS-25 exists to keep shut. It also survives a session reset,
  // which wipes the server's gm-next history, so "no gm-next" plus a stale cache
  // would otherwise resume a reset room mid-flow.
  const [stepIndex, setStepIndex] = useState(0);
  const [currentPlayerId, setCurrentPlayerId] = useState(null);

  // TAS-374: true until an authoritative source has told us where the room is.
  // Consumers show "Getting back to the room…" instead of a step we are only
  // guessing at. `stepConfirmedRef` is the same fact in ref form, read by the
  // apply paths below: while it is false the step pointer is a cache guess, so
  // an authoritative step may move us BACKWARD off it. Once true, the usual
  // forward-only rule resumes and the room can never be rewound by a stale read.
  const [isHydrating, setIsHydrating] = useState(() => Boolean(searchParams.get('code')));
  const stepConfirmedRef = useRef(false);
  const confirmStep = useCallback(() => {
    stepConfirmedRef.current = true;
    setIsHydrating(false);
  }, []);

  // When THIS device entered the current step. The shared anchor for any screen
  // whose entrance choreography is supposed to look the same around the table.
  //
  // Screens used to arm their reveals off their OWN readiness — data fetched,
  // video decoded, animation delay elapsed from mount — so the spread between
  // two phones was whatever the spread in their fetches was, which on venue wifi
  // is seconds. Anchoring here collapses that to the spread in step-advance
  // DELIVERY, which is tens of milliseconds. A screen that is late is then late
  // by its own load alone, and can seek its choreography forward instead of
  // replaying a beat the rest of the table has already watched.
  //
  // Deliberately a LOCAL clock reading and not the gm-next row's server
  // `created_at`: comparing a server timestamp against `Date.now()` is the clock
  // skew that made a phone count down from 73 (game 1705). The residual latency
  // term is the small one; per-device readiness, which this removes, was the
  // seconds-long one.
  //
  // Derived during render rather than in an effect on purpose. An effect lands
  // one commit late, so a screen mounting for the new step would read the
  // PREVIOUS step's anchor on its first render and conclude its choreography was
  // already over. This is React's documented adjust-state-during-render form —
  // not a ref mutated in the render body, which a discarded concurrent render
  // would leave behind — and it settles in one extra pass because the second
  // render sees the indexes match.
  //
  // Keyed by SESSION as well as index: this provider stays mounted when the
  // `code` query parameter changes, so a room entered at the same step index as
  // the last one would otherwise inherit the previous room's anchor — old enough
  // that the scoreboard arms on its floor and the blackout opens already settled.
  const stepAnchorKey = `${sessionCode || ''}:${stepIndex}`;
  const [stepEnteredAt, setStepEnteredAt] = useState(() => ({ key: stepAnchorKey, at: Date.now() }));
  if (stepEnteredAt.key !== stepAnchorKey) {
    setStepEnteredAt({ key: stepAnchorKey, at: Date.now() });
  }
  const stepEnteredAtMs = stepEnteredAt.at;
  const [isGm, setIsGm] = useState(() => {
    // TAS-274: the /tv big screen is a passive display, never the GM. A stale
    // `isGm=true` in the TV browser's localStorage (e.g. opened on the GM's own
    // device) would gate FinalLeaderboard's follower listeners off and break the
    // podium sync — so force it false in displayMode.
    if (displayMode) return false;
    // Tolerate a corrupt value — an unguarded JSON.parse in a render-time
    // initializer took the whole game down for that browser (TAS-64).
    return localStorage.getItem('isGm') === 'true';
  });
  const [wineQuestions, setWineQuestions] = useState([]);
  // TAS-357: true only once the session_questions_v2 query has resolved WITHOUT
  // error. A missing row is only knowable after that; before it (or after a
  // failed fetch) an absent currentQuestion still means "loading".
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  // TAS-415: true when the combined session/questions/wines load below failed
  // (any leg) and hasn't succeeded since. Nothing retried this on its own —
  // reload-mid-question left the phone (or the GM, which blocks the room) on
  // "Loading…" forever, because the recovery paths re-read live state, not the
  // catalogue. Mirrored into a ref so the recovery listeners (declared in a
  // later effect, closed over at mount) can read the latest value.
  const [initialLoadError, setInitialLoadError] = useState(false);
  const initialLoadErrorRef = useRef(false);
  const [wineList, setWineList] = useState([]);
  const [wineTypeMap, setWineTypeMap] = useState({}); // wineId -> 'Red' | 'White' | etc.
  const [wineBlendVarietalMap, setWineBlendVarietalMap] = useState({}); // wineId -> dominant varietal for blends
  const [currentWineId, setCurrentWineId] = useState(null);
  // The moment a wine starts, start on its 3D bottle: the reveal is minutes
  // away and the model is the heaviest thing it shows.
  useEffect(() => { warmBottleFor(currentWineId); }, [currentWineId]);
  const [wineIndex, setWineIndex] = useState(0);
  const [hideWineDetails, setHideWineDetails] = useState(true);
  const [isResuming, setIsResuming] = useState(false);
  const [resumedStepIndex, setResumedStepIndex] = useState(null);
  const [resultsIndex, setResultsIndex] = useState(0);
  const resumeLock = useRef(false);
  const stepIndexRef = useRef(0);
  const flowStepsRef = useRef([]);
  // Furthest step a burst of missed gm-next events has named, and the timer that
  // applies it. A reconnecting phone gets the whole backlog at once and should
  // land on the room's step, not walk to it.
  // Ticks whenever the room changes run — a host reset, or a different game.
  // Async work captures it before awaiting and drops itself if it no longer
  // matches, so a slow response cannot land inside a run it was not read from
  // (Codex: a delayed poll resurrected an old run; a catch-up already awaiting
  // assets applied an old step inside the new one).
  const runGenerationRef = useRef(0);
  const pendingCatchUpRef = useRef(null);
  const catchUpTimerRef = useRef(null);
  // How far the current step's asset preload has got, for the curtain's bar.
  const [prefetchProgress, setPrefetchProgress] = useState(null);
  const isAdvancingRef = useRef(0); // timestamp of the in-flight advance; 0 = idle (self-expiring lock, TAS-168)
  const syncToLatestStepRef = useRef(null); // Holds the current sync function for on-demand use
  const broadcastChannelRef = useRef(null); // Supabase Broadcast channel for instant sync
  // TAS-178 session_live_state reconcile (flag-gated): local view of the
  // authoritative room row's monotonicity key (run_id, version), seeded on first read.
  const liveStateVersionRef = useRef(0);
  const liveStateRunIdRef = useRef(null);
  // Part 2: the run id as state, so consumers re-render when the room resets.
  const [liveRunId, setLiveRunId] = useState(null);
  const bankLiveView = useCallback((runId, version) => {
    liveStateRunIdRef.current = runId;
    liveStateVersionRef.current = version;
    setLiveRunId((prev) => (prev === (runId ?? null) ? prev : (runId ?? null)));
  }, []);
  // Part 2 (H5): the attempt this phone last saw for each round id, from the
  // live row's payload. Cleared when the run changes.
  const roundAttemptsRef = useRef(new Map());
  // Runs this phone has moved on from (a reset). Rows naming one are late reads.
  const leftRunsRef = useRef(new Set());
  // The round named by the latest live row this phone accepted, and its
  // attempt: `{ roundId, attempt }` or nulls on non-round steps.
  const [liveRound, setLiveRound] = useState({ roundId: null, attempt: null });
  const [attemptTick, setAttemptTick] = useState(0);
  const roundRestartListenersRef = useRef(new Set());
  // Part 2 (H4): the last `gm_player_id` seen on a live row. `undefined` until
  // the first row, so the first sighting records rather than signals.
  const lastGmSignalRef = useRef(undefined);
  const checkGmRef = useRef(null);
  const refreshGmNameRef = useRef(null);
  // Part 2: an authoritative row from this phone's own RPC (H2, N2 guard inside).
  const applyAuthoritativeRef = useRef(null);
  // TAS-407: the reset row arrives from the initial read AND every resubscribe,
  // so the teardown below is latched — one navigation, not one per delivery.
  const sessionResetHandledRef = useRef(false);
  const liveStateReadRef = useRef(null); // re-read trigger for the flowSteps-load catch-up
  const applyLiveStateRef = useRef(null); // apply an authoritative row we were handed directly
  const sessionStatusRef = useRef(null); // sessions_v2.status, for the resume cache fallback
  // TAS-415: re-run the failed leg(s) of the session/questions/wines load. Set
  // while that effect is live (sessionCode present, not torn down), cleared
  // on cleanup, reset to fresh leg state on every new sessionCode.
  const runCatalogLoadRef = useRef(null);
  // Which of 'session' | 'questions' | 'wines' errored and hasn't succeeded
  // since — the source of truth `initialLoadError` is derived from.
  const failedLegsRef = useRef(new Set());
  // TAS-415 review (blockers #1/#2): per-leg single-flight + generation guard.
  // `inFlight` stops a second concurrent attempt at the SAME leg from ever
  // starting (focus and visibilitychange can both fire for one real refocus —
  // TAS-65). `generation` stops a stale attempt's LATE result from applying
  // over a newer attempt's result, or from re-marking a leg failed after a
  // later attempt already fixed it — the ordering guarantee `runGenerationRef`
  // gives the live-state reconcile below, but scoped to catalogue loads.
  const sessionLegRef = useRef({ inFlight: false, generation: 0 });
  const questionsLegRef = useRef({ inFlight: false, generation: 0 });
  const winesLegRef = useRef({ inFlight: false, generation: 0 });

  // Keep refs in sync with state
  useEffect(() => {
    stepIndexRef.current = stepIndex;
  }, [stepIndex]);

  useEffect(() => {
    flowStepsRef.current = flowSteps;
  }, [flowSteps]);

  // TAS-374: there used to be a "mount event snapshot" here — the created_at of
  // the latest gm-next at mount time — and both gm-next catch-up queries below
  // filtered on `created_at > snapshot`. It was meant to stop stale events from
  // a PREVIOUS run of the session advancing the step.
  //
  // It cannot: every reset path DELETES every game_events_v2 row for the session
  // and leaves at most a `gm-reset-anchor`, which neither query selects. There
  // are no stale gm-next rows for it to catch. The live-state row comes back
  // with a fresh run_id, and the reconcile handles the epoch change on its own
  // (resetToRun, TAS-181). Since migration 224 reset_test_session (the dev
  // button's RPC) REWRITES that row to phase 'session-reset' rather than
  // deleting it, which is the same signal reset_game_room has sent since 201 —
  // a deleted row reached nobody, because an absent one is ignored below.
  //
  // What the filter DID do was blind a remounted client to the entire game. On
  // a mid-game mount the snapshot IS the room's current step, so `created_at >
  // snapshot` matches nothing, forever — the focus/visibility resync could
  // never recover. That is the third leg of prod session 5017. Removed; the
  // forward-only `> stepIndexRef.current` guard is what keeps the step honest.

  // TAS-374: never strand anyone on the recovery screen. If nothing
  // authoritative has answered in this window the network is the problem, not
  // the step — fall through to the cache seed, which is the same fallback the
  // resume path has always used when the server has no gm-next. The step stays
  // UNCONFIRMED, so a late server answer can still correct it in either
  // direction.
  useEffect(() => {
    if (!isHydrating) return undefined;
    const id = setTimeout(() => {
      // Ask once more before falling back to the guess. Most "no answer yet" is
      // a slow read rather than a dead one, and because the step stays
      // UNCONFIRMED these still correct it in either direction when they land.
      try { liveStateReadRef.current?.('hydration-failsafe'); } catch { /* best-effort */ }
      try { syncToLatestStepRef.current?.('hydration-failsafe'); } catch { /* best-effort */ }
      setIsHydrating(false);
    }, 6000);
    return () => clearTimeout(id);
  }, [isHydrating]);

  // TAS-374: hydration is per-room. This provider stays mounted across a `code`
  // change, so a player arriving from a public route (no code) or switching
  // rooms must re-enter it — otherwise the second room inherits the first one's
  // step pointer and confirmed flag, which is the same wrong-screen bug by
  // another route.
  const hydratedForCodeRef = useRef(sessionCode);
  useEffect(() => {
    if (hydratedForCodeRef.current === sessionCode) return;
    hydratedForCodeRef.current = sessionCode;
    stepConfirmedRef.current = false;
    if (!sessionCode) {
      setIsHydrating(false);
      return;
    }
    setStepIndex(0);
    setIsHydrating(true);
  }, [sessionCode]);

  // On-demand sync: lets consumers (e.g., useAutoAdvance) trigger an immediate poll
  // without waiting for the 10-second interval. Useful for non-GM players on poor connections.
  const requestSync = useCallback(() => {
    if (syncToLatestStepRef.current) {
      syncToLatestStepRef.current('requestSync');
    }
  }, []);

  // Transition prefetch state
  const [isPrefetchingNext, setIsPrefetchingNext] = useState(false);
  const [prefetchMessage, setPrefetchMessage] = useState(
    'Preparing next screen...'
  );

  // Screen-ready gate: false while Game.jsx is loading assets/power-state for a
  // question step. GameWrapper holds its spinner up until this becomes true, then
  // Game.jsx fires playEntrance() directly after the spinner's exit animation.
  const [screenReady, setScreenReady] = useState(true);

  // Reset for every step change except screens that intentionally skip the overlay.
  // wine-review: overlay would block the rating input.
  // wine-reveal: overlay is suppressed at the advance call site too (no prefetch needed).
  useEffect(() => {
    if (!currentStep) return;
    const noGateTypes = ['wine-review', 'wine-reveal'];
    if (!noGateTypes.includes(currentStep.type)) {
      setScreenReady(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  // 8s failsafe — prevents being stuck forever if Game.jsx never signals ready
  useEffect(() => {
    if (screenReady) return;
    const id = setTimeout(() => setScreenReady(true), 8000);
    return () => clearTimeout(id);
  }, [screenReady]);

  // Allow consumers to force-hide the prefetch overlay once destination renders
  const endPrefetch = useCallback(() => setIsPrefetchingNext(false), []);

  // Results index manipulation functions
  const resetResultsIndex = useCallback(() => setResultsIndex(0), []);
  const incrementResultsIndex = useCallback(() => setResultsIndex(prev => prev + 1), []);

  // Prefetch helper: load images with a per-image timeout to prevent hanging
  const preloadImages = useCallback(async (urls, onProgress) => {
    // Reports progress as it goes, because the curtain shows it. Prod 4458
    // (19 Sep, slow Wi-Fi): option icons half-loaded and then broke, and a wine
    // reveal showed nothing — the per-image timeout expired, the curtain lifted
    // anyway, and the screen was left fetching its own pictures over a
    // connection that could not deliver them. One retry per image, because a
    // first failure on a weak network is usually the network.
    //
    // The budget follows the connection (TAS-398): eight seconds is generous on
    // wifi and far too short on the link 4458 actually had, where giving up is
    // the thing that produced a broken icon rather than a slow one.
    const tier = netTier();
    const IMAGE_TIMEOUT = tier === NET_SLOW ? 20000 : tier === NET_MIDDLE ? 12000 : 8000;
    const unique = Array.from(new Set(urls.filter(Boolean)));
    let done = 0;
    const total = unique.length;
    if (total && onProgress) onProgress({ loaded: 0, total });
    const loadOnce = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        let settled = false;
        const finish = (ok) => { if (!settled) { settled = true; resolve(ok); } };
        img.onload = () => finish(true);
        img.onerror = () => finish(false);
        img.src = src;
        setTimeout(() => finish(false), IMAGE_TIMEOUT);
      });
    // The whole batch has a budget, not just each picture. Two slow-tier
    // attempts at twenty seconds, queued two at a time, could hold the room on
    // the curtain for two minutes over five images — worse than the problem
    // this was trying to fix (Codex). When the budget runs out the game moves
    // on; the images already in flight keep downloading into the cache, so the
    // screen usually gets them a moment later anyway.
    const budgetMs = tier === NET_SLOW ? 25000 : tier === NET_MIDDLE ? 18000 : 12000;
    let budgetTimer = null;
    const budget = new Promise((resolve) => { budgetTimer = setTimeout(resolve, budgetMs); });
    const settle = async (work) => {
      try { await Promise.race([work, budget]); }
      finally { if (budgetTimer) clearTimeout(budgetTimer); }
    };

    const one = async (src) => {
      const ok = await loadOnce(src);
      if (!ok) await loadOnce(src);
      done += 1;
      if (onProgress) onProgress({ loaded: done, total });
    };

    // On a thin pipe, asking for twelve pictures at once means twelve pictures
    // arrive slowly and the timeout takes them all. In a queue the first ones
    // land early, so the screen has most of what it needs even if the tail
    // expires — and the progress bar moves, which is the honest part.
    const concurrency = tier === NET_SLOW ? 2 : tier === NET_MIDDLE ? 4 : total;
    if (concurrency >= total) {
      await settle(Promise.all(unique.map(one)));
      return;
    }
    const queue = [...unique];
    await settle(Promise.all(
      Array.from({ length: concurrency }, async () => {
        while (queue.length) {
          const src = queue.shift();
          if (src) await one(src);
        }
      }),
    ));
  }, []);

  // Prefetch assets for a step
  const prefetchStepAssets = useCallback(
    async (step) => {
      try {
        if (!step) return;
        if (sessionCode && ['reveal', 'reveal-answer', 'results', 'scoreboard', 'wine-reveal'].includes(step.type)) {
          const { data, error } = await supabase.rpc('game_questions', { p_session_id: sessionCode });
          if (error) throw error;
          if (Array.isArray(data)) setWineQuestions((questions) => mergeQuestionFeedback(questions, data));
        }
        // Caller manages isPrefetchingNext visibility timeline
        setPrefetchMessage('Preparing next screen...');
        const t0 = Date.now();
        debugLog('[GF] PREFETCH_START', {
          step,
          time: t0,
          iso: new Date(t0).toISOString(),
        });

        if (
          step.type === 'wine-intro' ||
          (step.type === 'intro' && step.wine && !step.gameType)
        ) {
          // Preload the round's bottle. The explainer carousel this used to
          // fetch (intro/1-5 in storage) left the screen long ago, so the
          // curtain was holding for five pictures nobody sees.
          const urls = [roundBottleForStep(step, wineList, wineTypeMap)];
          debugLog('[GF] PREFETCH_WINE_INTRO_URLS', urls);
          await preloadImages(urls, setPrefetchProgress);
        } else if (step.type === 'question') {
          // Preload question option images and hint image
          const q = wineQuestions.find(
            (qq) =>
              qq.wine_id === step.wine && qq.round_number === step.index + 1
          );
          if (q) {
            setPrefetchMessage('Loading question assets...');
            const urls = (q.options || [])
              .map((opt) => q.metadata?.[opt]?.image)
              .filter(Boolean);
            if (q.hintImage) urls.push(q.hintImage);
            debugLog('[GF] PREFETCH_QUESTION_URLS', urls);
            await preloadImages(urls, setPrefetchProgress);
          }
        } else if (step.type === 'reveal' || step.type === 'reveal-answer') {
          const q = wineQuestions.find(
            (qq) =>
              qq.wine_id === step.wine && qq.round_number === step.index + 1
          );
          if (q) {
            setPrefetchMessage('Loading reveal assets...');
            const urls = (q.options || [])
              .map((opt) => q.metadata?.[opt]?.image)
              .filter(Boolean);
            debugLog('[GF] PREFETCH_REVEAL_URLS', urls);
            await preloadImages(urls, setPrefetchProgress);
          }
        }
      } finally {
        // The bar belongs to one step's load; clear it so the next screen
        // never inherits a stale one.
        setPrefetchProgress(null);
        const t1 = Date.now();
        debugLog('[GF] PREFETCH_END', {
          time: t1,
          iso: new Date(t1).toISOString(),
        });
        // Overlay visibility managed by caller
      }
    },
    [preloadImages, wineQuestions, sessionCode, wineList, wineTypeMap]
  );

  // Stable ref so broadcast/fallback handlers always call the latest prefetchStepAssets.
  // Those effects don't list prefetchStepAssets in their deps to avoid channel teardown.
  const prefetchStepAssetsRef = useRef(prefetchStepAssets);
  useEffect(() => {
    prefetchStepAssetsRef.current = prefetchStepAssets;
  }, [prefetchStepAssets]);

  // TAS-374: warm the step the cache says we were on, while the recovery screen
  // is up and the server is being asked. This is what makes coming back feel
  // instant without letting the cache decide anything: if the server agrees the
  // assets are already decoded when the screen mounts, and if it disagrees we
  // threw away a prefetch and nothing else.
  useEffect(() => {
    if (!isHydrating || !sessionCode || flowSteps.length === 0) return;
    const likely = readCachedStep(sessionCode);
    if (likely <= 0 || likely >= flowSteps.length) return;
    Promise.resolve(prefetchStepAssetsRef.current?.(flowSteps[likely])).catch(() => {});
  }, [isHydrating, sessionCode, flowSteps]);

  // Debug logging for state changes - only in development
  // Perf: Removed production debug logging to reduce re-render overhead

  // Perf: Removed flow steps debug logging to reduce re-render overhead

  // Centralized GM name fetch
  const [gmName, setGmName] = useState(
    () => localStorage.getItem('gmName') || 'GM'
  );
  // Part 2 (H4): a callback, not a one-shot effect, so a GM change seen on the
  // live row can refresh the name without a remount.
  const gmNameSeqRef = useRef(0);
  const refreshGmName = useCallback(async () => {
    if (!sessionCode) return;
    const mine = ++gmNameSeqRef.current;
    const { data } = await supabase
      .from('session_users_v2')
      .select('username')
      .eq('session_id', sessionCode)
      .eq('is_gamemaster', true)
      .single();
    if (mine !== gmNameSeqRef.current) return; // a newer refresh owns the answer
    if (data?.username) {
      setGmName(data.username);
      localStorage.setItem('gmName', data.username);
    }
  }, [sessionCode]);
  useEffect(() => {
    refreshGmNameRef.current = refreshGmName;
  }, [refreshGmName]);
  useEffect(() => {
    if (!sessionCode) return;
    refreshGmName().catch(() => {});
  }, [sessionCode, refreshGmName]);

  // Effect to update sessionCode if URL search parameter changes
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl !== sessionCode) {
      setSessionCode(codeFromUrl);
      debugLog(
        `📦 GameFlowProvider: sessionCode updated via useEffect from URL: ${codeFromUrl}`
      );
    }
  }, [searchParams, sessionCode]); // sessionCode added to dependencies

  useEffect(() => {
    // TAS-274: never rehydrate GM on the /tv display — it's always a follower.
    if (displayMode) { setIsGm(false); return; }
    const gm = localStorage.getItem('isGm');
    if (gm !== null) {
      setIsGm(gm === 'true');
      debugLog('♻️ Rehydrated isGm from localStorage:', gm);
    }
  }, [displayMode]);

  useEffect(() => {
    // A14: this game's id (the mount-time code; later changes are handled by
    // the session-switch effect).
    const id = playerStorage.get(sessionCode ?? undefined);
    if (id) {
      setCurrentPlayerId(id);
      debugLog('📦 Loaded currentPlayerId from localStorage:', id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Combined data loading effect - runs all session data queries in parallel
  // This significantly improves initial load time by avoiding sequential fetches
  useEffect(() => {
    if (!sessionCode) {
      setSessionDataLoaded(false);
      setQuestionsLoaded(false);
      return;
    }
    let cancelled = false;
    setSessionDataLoaded(false);
    setQuestionsLoaded(false);
    // TAS-415 review: leg state belongs to ONE sessionCode — a fresh effect
    // run (session changed, or first mount) must not inherit a stale
    // in-flight flag or generation from whatever the previous sessionCode
    // was doing.
    failedLegsRef.current = new Set();
    sessionLegRef.current = { inFlight: false, generation: 0 };
    questionsLegRef.current = { inFlight: false, generation: 0 };
    winesLegRef.current = { inFlight: false, generation: 0 };
    setInitialLoadError(false);
    initialLoadErrorRef.current = false;

    const updateInitialLoadError = () => {
      const hasError = failedLegsRef.current.size > 0;
      setInitialLoadError(hasError);
      initialLoadErrorRef.current = hasError;
    };

    // TAS-415 review (blockers #1/#2): loads only the requested legs — the
    // initial mount below asks for all three; a retry asks only for
    // whichever leg(s) failed, so a wines-only failure never re-touches
    // wineQuestions at all. A leg already in flight is dropped from THIS
    // attempt (the in-flight one owns that leg's outcome); if that empties
    // the request, there is nothing new to start. Each included leg also
    // gets its own generation stamp, checked before its result is applied,
    // so an attempt superseded by a later one for the SAME leg can never
    // overwrite what the later one already did — the ordering guarantee
    // runGenerationRef gives the live-state reconcile elsewhere in this
    // file, scoped here to the catalogue load instead of the room's run.
    async function runCatalogLoad(requested) {
      // TAS-415 review round 3 (blocker #1): capture the CURRENT leg-state
      // objects now and use ONLY these locals for the rest of this attempt —
      // never `sessionLegRef.current` etc. again below this point. The reset
      // above REPLACES `xLegRef.current` wholesale on every new sessionCode;
      // an attempt that started under the OLD session and kept reading
      // `.current` after that point (its `finally`, which runs even after a
      // cancelled early-return) would silently start mutating the NEW
      // session's fresh in-flight/generation state instead of its own,
      // now-orphaned one — a hung fetch from a room you already left could
      // clear the in-flight flag a live retry in the NEW room was relying on.
      // Capturing the object reference means a stale attempt can only ever
      // mutate the object it started with, which nothing else reads once the
      // ref has moved on.
      const sessionLegObj = sessionLegRef.current;
      const questionsLegObj = questionsLegRef.current;
      const winesLegObj = winesLegRef.current;
      const legs = {
        session: Boolean(requested.session) && !sessionLegObj.inFlight,
        questions: Boolean(requested.questions) && !questionsLegObj.inFlight,
        wines: Boolean(requested.wines) && !winesLegObj.inFlight,
      };
      if (!legs.session && !legs.questions && !legs.wines) return;

      debugLog('🔍 Loading session data leg(s):', legs);
      const startTime = Date.now();

      const sessionGen = legs.session ? ++sessionLegObj.generation : null;
      const questionsGen = legs.questions ? ++questionsLegObj.generation : null;
      const winesGen = legs.wines ? ++winesLegObj.generation : null;
      if (legs.session) sessionLegObj.inFlight = true;
      if (legs.questions) questionsLegObj.inFlight = true;
      if (legs.wines) winesLegObj.inFlight = true;

      try {
        // Run the requested queries in parallel; a leg this attempt was not
        // asked for resolves to null immediately and is never inspected below.
        const [sessionResult, questionsResult, winesResult] = await Promise.all([
          legs.session
            ? supabase
                .from('sessions_v2')
                .select('flow_steps, game_type, game_mode, type, prizes, features, status')
                .eq('id', sessionCode)
                .single()
            : Promise.resolve(null),
          legs.questions
            ? supabase.rpc('game_questions', { p_session_id: sessionCode })
            : Promise.resolve(null),
          legs.wines
            ? supabase
                .from('session_wines_v2')
                .select('wine_id, wines_v2(type, blend_varietal)')
                .eq('session_id', sessionCode)
                .order('created_at')
            : Promise.resolve(null),
        ]);

        if (cancelled) return;

        const loadTime = Date.now() - startTime;
        debugLog(`✅ Session data leg(s) loaded in ${loadTime}ms`, legs);

        // Process session data
        if (legs.session && sessionLegObj.generation === sessionGen) {
          if (!sessionResult.error && sessionResult.data) {
            debugLog('✅ Session data:', {
              gameType: sessionResult.data.game_type,
              flowStepsCount: sessionResult.data.flow_steps?.length || 0,
            });
            setFlowSteps(sessionResult.data.flow_steps || []);
            setSessionPrizes(sessionResult.data.prizes ?? null);
            setGameType(sessionResult.data.game_type || 'single');
            // party-normal uses party scoring features with a normal-style flow;
            // treat it as 'party' so all feature gates activate automatically.
            const rawMode = sessionResult.data.game_mode || 'party';
            const resolvedMode = rawMode === 'party-normal' ? 'party' : rawMode;
            setGameMode(resolvedMode);
            // Track the resolved game mode as a super-property on all subsequent events.
            setSessionContext({ game_mode: resolvedMode });
            reinitForSessionType(sessionResult.data.type || 'test');
            setFeatures(sessionResult.data.features || {});
            // A host who already knows the venue is rough can cap the whole room from
            // the admin (features.assetTier). It can only make the evening lighter —
            // a cap never pushes a phone that measured slow back up.
            setSessionTierCap(sessionResult.data.features?.assetTier ?? null);
            // TAS-374: lifecycle is 'waiting' → 'lobby' → 'started' → 'ended'.
            // A reset puts the room back to 'waiting', which the local step cache
            // has no way of knowing — see the resume fallback below.
            sessionStatusRef.current = sessionResult.data.status ?? null;
            failedLegsRef.current.delete('session');
          } else if (sessionResult.error) {
            console.error('❌ Failed to load session:', sessionResult.error);
            failedLegsRef.current.add('session');
          }
        }

        // Process questions data
        if (legs.questions && questionsLegObj.generation === questionsGen) {
          if (!questionsResult.error && questionsResult.data) {
            const enriched = enrichQuestions(questionsResult.data);
            setWineQuestions((prev) => {
              // TAS-415 review (blocker #1): merge, never blind-replace — the
              // reveal's own prefetch (mergeQuestionFeedback, imported above)
              // can land mid-retry and unlock correct_answer/feedback into
              // `prev`; this must not be allowed to clobber that with an
              // older/redacted snapshot.
              const merged = mergeQuestionsPreservingFeedback(prev, enriched);
              // TAS-415 review (note): skip the setState when nothing
              // feedback-relevant changed — wineQuestions is a dependency of
              // downstream effects (Game.jsx's asset prefetch among them),
              // and a same-content replace was restarting a 5s image load
              // for a retry that only confirmed what we already had.
              if (
                prev.length === merged.length &&
                questionsFeedbackSignature(prev) === questionsFeedbackSignature(merged)
              ) {
                return prev;
              }
              return merged;
            });
            debugLog('✅ Questions loaded:', { count: enriched.length });
            setQuestionsLoaded(true);
            failedLegsRef.current.delete('questions');
          } else if (questionsResult.error) {
            console.error('❌ Failed to load questions:', questionsResult.error);
            // TAS-415: this is the leg that actually strands the player — an
            // empty wineQuestions leaves currentQuestion unresolved forever, and
            // questionsLoaded never flips true, so missingQuestion stays false
            // and Game.jsx's loading guard never lets go.
            failedLegsRef.current.add('questions');
          }
        }

        // Process wines data
        if (legs.wines && winesLegObj.generation === winesGen) {
          if (!winesResult.error && winesResult.data) {
            const wineIds = winesResult.data.map((sw) => sw.wine_id);
            const typeMap = {};
            const blendVarietalMap = {};
            winesResult.data.forEach((sw) => {
              if (sw.wine_id && sw.wines_v2?.type) {
                typeMap[sw.wine_id] = sw.wines_v2.type;
              }
              if (sw.wine_id && sw.wines_v2?.blend_varietal) {
                blendVarietalMap[sw.wine_id] = sw.wines_v2.blend_varietal;
              }
            });
            debugLog('✅ Wines loaded:', { count: wineIds.length });
            setWineList(wineIds);
            // Pull the opening wines' assets while the room is still filling up
            // (TAS-400). Deliberately not awaited: nothing in the lobby waits on
            // it, and a phone that leaves early has lost nothing. Only before the
            // game starts — mid-game the per-step prefetch owns the connection
            // and a second downloader would be competing with it. A wines-only
            // retry doesn't refetch the session, so fall back to the last
            // known status rather than a sessionResult this attempt never asked for.
            const status = legs.session
              ? (sessionResult.data?.status ?? 'waiting')
              : (sessionStatusRef.current ?? 'waiting');
            if (status === 'waiting') {
              void warmRevealAssets(sessionCode, wineIds);
            }
            setWineTypeMap(typeMap);
            setWineBlendVarietalMap(blendVarietalMap);
            // Set initial wine if we have wines
            if (wineIds.length > 0) {
              setCurrentWineId((prev) => prev || wineIds[0]);
              setWineIndex((prev) => (prev === 0 && !currentWineId ? 0 : prev));
            }
            failedLegsRef.current.delete('wines');
          } else if (winesResult.error) {
            console.error('❌ Failed to load wines:', winesResult.error);
            failedLegsRef.current.add('wines');
          }
        }

        // TAS-415 review round 3 (note): release this attempt's in-flight
        // guard and recompute initialLoadError HERE — before the step-0
        // prefetch below, which can take up to ~5s — not after. Until this
        // ran, every leg this attempt owns still reported "in flight" (so a
        // recovery signal arriving during the prefetch found nothing to
        // retry via single-flight) AND failedLegsRef/initialLoadError had
        // not been recomputed yet (so the SAME signal, even if it got past
        // single-flight some other way, would see initialLoadError still
        // false for a leg that had, in fact, just failed).
        // Final review: only the attempt that still OWNS a leg may release
        // it. Releasing early (the point of this block) means a retry can take
        // the leg over while this attempt is still inside the prefetch below;
        // when this attempt then reached its `finally`, it cleared the RETRY's
        // in-flight flag, a second recovery signal started another retry, and
        // the first retry's success was generation-dropped. Same-session,
        // same leg object — the captured locals alone did not cover it.
        if (legs.session && sessionLegObj.generation === sessionGen) sessionLegObj.inFlight = false;
        if (legs.questions && questionsLegObj.generation === questionsGen) questionsLegObj.inFlight = false;
        if (legs.wines && winesLegObj.generation === winesGen) winesLegObj.inFlight = false;
        if (!cancelled) updateInitialLoadError();

        // Prefetch step 0 assets so the first game screen enters without
        // pop-in, then signal App.jsx the game is ready to reveal. Only the
        // initial (all-three-legs) load does this — a leg-scoped retry runs
        // mid-game, long after step 0 has been left, and re-warming it would
        // just compete with whatever step the room is actually prefetching.
        if (legs.session && legs.questions && legs.wines) {
          const steps = sessionResult.data?.flow_steps || [];
          if (!cancelled && steps.length > 0) {
            try {
              await prefetchStepAssetsRef.current(steps[0]);
            } catch {
              // Non-fatal: prefetch failure shouldn't block the game from loading
            }
          }
        }

        if (!cancelled) {
          setSessionDataLoaded(true);
          window.dispatchEvent(new Event('game-session-ready'));
        }
      } catch (error) {
        if (!cancelled) {
          console.error('❌ Failed to load session data:', error);
          // Only the legs THIS attempt owns (and is still the newest attempt
          // for) get marked failed — an unrelated exception must not flag a
          // leg a different, still-in-flight attempt is about to resolve.
          if (legs.session && sessionLegObj.generation === sessionGen) failedLegsRef.current.add('session');
          if (legs.questions && questionsLegObj.generation === questionsGen) failedLegsRef.current.add('questions');
          if (legs.wines && winesLegObj.generation === winesGen) failedLegsRef.current.add('wines');
          updateInitialLoadError();
          // Still signal ready so LoadingScreen doesn't stay stuck
          setSessionDataLoaded(true);
          window.dispatchEvent(new Event('game-session-ready'));
        }
      } finally {
        // Blanket safety net for exit paths above that don't already clear
        // these (the `cancelled` early-return right after Promise.all, or
        // any exception thrown outside what the catch block's own generation
        // checks anticipate) — redundant-but-harmless on the normal path,
        // which already cleared these on the captured locals before the
        // prefetch await. Still the captured locals, per blocker #1 above:
        // never `xLegRef.current` here — and still generation-guarded, so a
        // retry that took the leg over during the prefetch keeps its flag.
        if (legs.session && sessionLegObj.generation === sessionGen) sessionLegObj.inFlight = false;
        if (legs.questions && questionsLegObj.generation === questionsGen) questionsLegObj.inFlight = false;
        if (legs.wines && winesLegObj.generation === winesGen) winesLegObj.inFlight = false;
      }
    }

    // TAS-415: expose this run so recovery listeners and an explicit Retry
    // control can re-fire the failed leg(s) without duplicating the fetch/
    // enrich logic.
    runCatalogLoadRef.current = runCatalogLoad;
    runCatalogLoad({ session: true, questions: true, wines: true });
    return () => {
      cancelled = true;
      runCatalogLoadRef.current = null;
    };
  }, [sessionCode]);

  // Part 2 (H4): lifted out of its effect so a GM change on the live row can
  // re-run it. It reads the viewer's OWN is_gamemaster row — the GM identity
  // never comes from live.gm_player_id (N8), which is only the change signal.
  // A sequence number drops an older check's answer that lands after a newer
  // one's, so a slow read can't undo a promotion or a demotion.
  const checkGmSeqRef = useRef(0);
  const checkGm = useCallback(async () => {
    if (!sessionCode || !currentPlayerId) return;
    const mine = ++checkGmSeqRef.current;
    {
      const { data, error } = await supabase
        .from('session_users_v2')
        .select('id, is_gamemaster')
        .eq('session_id', sessionCode)
        .eq('id', currentPlayerId)
        .maybeSingle();
      if (mine !== checkGmSeqRef.current) return;

      debugLog('🔍 GM DB check — currentPlayerId:', currentPlayerId);
      debugLog('🔍 GM DB check — returned data:', data);

      if (error || !data) {
        debugWarn('⚠️ Could not verify GM status from DB:', {
          error: error?.message || error,
          code: error?.code,
          hasData: !!data,
          currentPlayerId,
        });
        return;
      }

      if (data.id !== currentPlayerId) {
        debugWarn(
          '❌ DB returned mismatched user row. Skipping GM override.'
        );
        return;
      }

      if (data.is_gamemaster === true) {
        setIsGm(true);
        localStorage.setItem('isGm', 'true');
        debugLog('🧙‍♂️ Verified GM status from DB:', true);
        // TAS-190: claim this GM's signed advance token and cache it per session
        // (survives reconnect on the same player_id). advance_session_v2 requires
        // it once require_gm_secret is enabled; claiming early means the flag flip
        // needs no extra round-trip. Migration 059 lets the current GM (re)bind, so
        // a new/re-elected GM claims here even if a stale secret is cached — force
        // a re-claim so a rotated-out secret doesn't linger. Best-effort.
        await claimAndStoreGmSecret(sessionCode, currentPlayerId, { force: true });
      } else {
        // TAS-20: the DB is authoritative in BOTH directions. Previously this only
        // ever PROMOTED (never demoted), so a device that was GM in a prior session
        // — carrying a stale localStorage `isGm=true` — stayed "GM" here, letting
        // two devices drive the room, and a client could self-promote with
        // localStorage.setItem('isGm','true'). By game-flow mount time GM assignment
        // is settled (we already returned early on any error / missing / mismatched
        // row above), so a confident is_gamemaster=false is a real "not GM" → demote.
        setIsGm(false);
        localStorage.removeItem('isGm');
        gmSecretStorage.clear(sessionCode);
        debugLog('🧙‍♂️ DB says not GM — demoting local isGm');
      }
    }
  }, [sessionCode, currentPlayerId]);
  useEffect(() => {
    checkGmRef.current = checkGm;
  }, [checkGm]);

  useEffect(() => {
    if (!sessionCode || !currentPlayerId) return;
    checkGm();
  }, [sessionCode, currentPlayerId, isGm, checkGm]); // Added isGm to dependency array

  // Part 2 (H4): every live row the provider sees — realtime, read, handed or
  // authoritative, including rows it banks without moving — is checked for a
  // change of `gm_player_id`. On a change the viewer's own GM status is re-read
  // (promote or demote, force-claiming a fresh GM secret on promotion) and the
  // GM's name is refreshed, so a handover reaches every phone without a reload.
  // useAutoAdvance's became-GM path then re-arms for the current round.
  const noteGmSignal = useCallback((row) => {
    if (!row || !Object.prototype.hasOwnProperty.call(row, 'gm_player_id')) return;
    const next = row.gm_player_id ?? null;
    if (lastGmSignalRef.current === undefined) {
      lastGmSignalRef.current = next;
      return;
    }
    if (next === lastGmSignalRef.current) return;
    lastGmSignalRef.current = next;
    debugLog('[GF] GM change seen on the live row — re-checking GM status');
    Promise.resolve(checkGmRef.current?.()).catch(() => {});
    Promise.resolve(refreshGmNameRef.current?.()).catch(() => {});
  }, []);

  // Part 2: per-room state is per room. The provider stays mounted across a
  // `code` change, so the next room must not inherit this one's attempts or
  // GM signal.
  useEffect(() => {
    roundAttemptsRef.current = new Map();
    leftRunsRef.current = new Set();
    lastGmSignalRef.current = undefined;
    setLiveRound({ roundId: null, attempt: null });
    setAttemptTick((t) => t + 1);
  }, [sessionCode]);

  const reloadViewModeRef = useRef(null);

  // Is TV mode available to THIS room? The admin flags it per session
  // (`features.tv_mode`, set in the create-session wizard); a device override or
  // the build flag can still force it either way. Undefined until the session
  // loads, which is why consumers gate on `sessionDataLoaded` before acting on
  // a `false` — see GameViewMode's auto-skip.
  const tvModeEnabled = useMemo(() => isTvModeEnabled(features), [features]);

  // TAS-274: track the room-wide Game View Mode (phone/tv). Mirrors the
  // participation-mode pattern — latest `game-view-mode-set` event wins, kept in
  // sync via a resilient channel. Whole effect no-ops (mode stays 'phone') when
  // TV mode is off for this session, so Phone mode is unchanged.
  useEffect(() => {
    if (!sessionCode || !tvModeEnabled) {
      setGameViewMode('phone');
      return undefined;
    }

    let disposed = false;
    const loadLatestViewMode = () => {
      supabase
        .from('game_events_v2')
        .select('payload')
        .eq('session_id', sessionCode)
        .eq('event_type', 'game-view-mode-set')
        .order('created_at', { ascending: false })
        .limit(1)
        .then(({ data }) => {
          if (disposed) return;
          const mode = data?.[0]?.payload?.mode;
          if (mode === 'tv' || mode === 'phone') setGameViewMode(mode);
        });
    };
    loadLatestViewMode();
    reloadViewModeRef.current = loadLatestViewMode;

    const dispose = resilientChannel({
      topic: () => `game-view-mode-${sessionCode}:${Date.now()}`,
      meta: { sessionCode, context: 'game-view-mode' },
      setup: (channel) => channel.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'game_events_v2', filter: `session_id=eq.${sessionCode}` },
        (payload) => {
          if (payload.new?.event_type === 'game-view-mode-set') {
            const mode = payload.new.payload?.mode;
            if (mode === 'tv' || mode === 'phone') setGameViewMode(mode);
          }
        }
      ),
      onStatus: (status) => {
        if (status === 'SUBSCRIBED') loadLatestViewMode();
      },
    });

    return () => { disposed = true; reloadViewModeRef.current = null; dispose(); };
  }, [sessionCode, tvModeEnabled]);

  // A player who missed the single `game-view-mode-set` insert advanced with the
  // room still in 'phone' mode, rendering full phone screens through a TV game.
  useCatchUpOnRecovery(
    Boolean(sessionCode) && tvModeEnabled,
    useCallback(() => reloadViewModeRef.current?.(), []),
  );

  // Real-time listener using Supabase Broadcast for instant sync (~50-100ms vs ~500ms-2s for Postgres Changes)
  // This is the primary sync mechanism - much faster than database-triggered events.
  //
  // TAS-168: the channel is rebuilt with backoff on CHANNEL_ERROR/TIMED_OUT.
  // A subscribed channel can never be re-subscribed, and without this a single
  // transport failure killed the GM's broadcasts AND every player's primary
  // listener for the rest of the session (prod 3827: one drop every 1-5 min).
  useEffect(() => {
    if (!sessionCode) return;

    debugLog('[GF] Setting up Broadcast channel for instant sync:', sessionCode);

    const channelName = `game-sync:${sessionCode}`;
    let disposed = false;
    // TAS-271 PR-5: sync-request storm cut. Track whether we have EVER
    // subscribed (a first subscribe has no gap to fill) and single-flight the
    // jittered request timer.
    let hadSubscribed = false;
    let syncRequestTimer = null;

    // C4 (edge-case audit 2026-09-28): on resilientChannel, like the other
    // page channels. The hand-rolled loop that was here reconnected without
    // jitter (a whole party on one access point resubscribed in lockstep) and
    // never revived after its five attempts. The topic is shared with every
    // other client (broadcast semantics), so it is a STRING: the helper awaits
    // removal of the stale instance before recreating it (TAS-171).
    const dispose = resilientChannel({
      topic: channelName,
      config: {
        config: {
          broadcast: { self: true }, // GM receives own broadcasts for confirmation
        },
      },
      meta: { sessionCode, context: 'gameflow-broadcast' },
      onChannel: (channel) => { broadcastChannelRef.current = channel; },
      setup: (channel) => {
        const reconcileSignal = createBroadcastReconciler(() => {
          if (!disposed) return liveStateReadRef.current?.('broadcast');
        });
        channel
          .on('broadcast', { event: 'step-advance' }, reconcileSignal)
          .on('broadcast', { event: 'sync-request' }, ({ payload }) => {
            // GM responds to sync requests from players who may have missed broadcasts
            if (isGm && stepIndexRef.current !== undefined) {
              debugLog('[GF] Responding to sync request from:', payload?.playerId);
              channel.send({
                type: 'broadcast',
                event: 'sync-response',
                payload: {
                  stepIndex: stepIndexRef.current,
                  targetPlayerId: payload?.playerId,
                  timestamp: Date.now(),
                },
              });
            }
          })
          .on('broadcast', { event: 'sync-response' }, reconcileSignal);
      },
      onStatus: (status) => {
        if (disposed) return;
        const channel = broadcastChannelRef.current;
        if (status === 'SUBSCRIBED' && channel) {
          debugLog('[GF] Broadcast channel connected for session:', sessionCode);
          // Non-GM: request the current step in case we missed step-advance
          // broadcasts while the channel was down. TAS-271 PR-5: only on a
          // REsubscribe (first-mount catch-up is covered by the resume effect,
          // gm-next re-check, and live-state read), and jittered 0-2s so a
          // mass reconnect doesn't burst the GM with N simultaneous requests
          // (~2N² broadcast deliveries at 16 players).
          const sendSyncRequest = () => channel.send({
            type: 'broadcast',
            event: 'sync-request',
            payload: { playerId: playerStorage.get(sessionCode), timestamp: Date.now() },
          });
          if (isSyncGatingEnabled()) {
            if (shouldSendSyncRequest({ isGm, hadSubscribed })) {
              if (syncRequestTimer) clearTimeout(syncRequestTimer);
              syncRequestTimer = setTimeout(() => {
                if (disposed || channel !== broadcastChannelRef.current) return;
                sendSyncRequest();
              }, jitterDelay(2000));
            }
          } else if (!isGm) {
            sendSyncRequest();
          }
          hadSubscribed = true;
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          debugWarn('[GF] Broadcast channel error/timeout:', status);
        }
      },
    });

    return () => {
      debugLog('[GF] Cleaning up Broadcast channel');
      disposed = true;
      if (syncRequestTimer) clearTimeout(syncRequestTimer);
      dispose();
    };
  }, [sessionCode, isGm]);

  // Hard fallback: postgres_changes on gm-next inserts.
  // Fires whenever the GM's DB write lands — catches cases where the broadcast was
  // missed entirely (e.g. channel not yet subscribed, network blip, etc.).
  // TAS-168: rebuilt on CHANNEL_ERROR/TIMED_OUT like the broadcast channel
  // above — a fallback that dies on the first transport failure isn't one.
  useEffect(() => {
    if (!sessionCode) return undefined;

    // C4 (edge-case audit 2026-09-28): on resilientChannel. This fallback
    // reconnected with no jitter and no wake-up revival. TAS-171: a unique
    // topic per subscription — supabase.channel(topic) returns any
    // still-registered instance for a repeated topic, and
    // .on('postgres_changes')/.subscribe() on it throws (TAS-150 class).
    // postgres_changes delivery doesn't need a shared topic name.
    const dispose = resilientChannel({
      topic: () => `gm-next-fallback:${sessionCode}:${++gmNextFallbackSeq}`,
      meta: { sessionCode, context: 'gameflow-gm-next-fallback' },
      setup: (channel) => channel.on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'game_events_v2', filter: `session_id=eq.${sessionCode}` },
        async (payload) => {
          const ev = payload?.new;
          if (ev?.event_type !== 'gm-next') return;
          // Part 2 (spec §5 Following the room): with step authority on, a
          // gm-next insert is a HINT, never a step. Re-read session_live_state
          // (coalesced, like the legacy burst handling below) and let the row
          // decide. Only a session with no live row falls through to legacy.
          if (stepAuthorityRef.current) {
            clearTimeout(catchUpTimerRef.current);
            catchUpTimerRef.current = setTimeout(async () => {
              const found = await liveStateReadRef.current?.('gm-next-hint');
              if (found) return;
              const target = ev?.payload?.stepIndex;
              const flow = flowStepsRef.current;
              if (typeof target !== 'number' || target <= stepIndexRef.current || !flow[target]) return;
              const generation = runGenerationRef.current;
              setIsPrefetchingNext(true);
              await prefetchStepAssetsRef.current(flow[target]);
              if (target <= stepIndexRef.current || runGenerationRef.current !== generation) return;
              setStepIndex(target);
            }, 150);
            return;
          }
          const nextIndex = ev?.payload?.stepIndex;
          const steps = flowStepsRef.current;
          if (typeof nextIndex === 'number' && steps.length > 0 && nextIndex > stepIndexRef.current) {
            // Land on where the room IS, not on each step it passed through.
            // A phone that missed six steps gets six of these at once when
            // the socket returns; applying them one at a time walked the
            // player forward question by question (prod 4458) and paid for a
            // prefetch on every hop.
            pendingCatchUpRef.current = Math.max(pendingCatchUpRef.current ?? -1, nextIndex);
            clearTimeout(catchUpTimerRef.current);
            catchUpTimerRef.current = setTimeout(async () => {
              const target = pendingCatchUpRef.current;
              pendingCatchUpRef.current = null;
              const generation = runGenerationRef.current;
              const flow = flowStepsRef.current;
              if (typeof target !== 'number' || target <= stepIndexRef.current || !flow[target]) return;
              debugLog('[GF] postgres_changes gm-next fallback — applying step:', target);
              setIsPrefetchingNext(true);
              await prefetchStepAssetsRef.current(flow[target]);
              // Re-check AFTER the prefetch: the socket or a poll can apply a
              // newer step while assets load, and writing ours then walks the
              // player backwards (Codex).
              if (target <= stepIndexRef.current) return;
              // And the run may have reset while we loaded. Step 12 of the
              // old game is not step 12 of the new one; clearing the timer
              // could never stop a callback that had already started.
              if (runGenerationRef.current !== generation) return;
              setStepIndex(target);
            }, 150);
          }
        }
      ),
    });

    return dispose;
  }, [sessionCode]);

  // Re-check latest gm-next when flowSteps are available (handles missed early events).
  // TAS-374: unfiltered — see the note where the mount snapshot used to live.
  // This is also the query that settles hydration: gm-next is the authority the
  // step cache defers to (TAS-25), so an answer from it — even "there is no
  // gm-next yet" — is what promotes the step pointer from guess to fact.
  useEffect(() => {
    if (!sessionCode || !Array.isArray(flowSteps) || flowSteps.length === 0) {
      return;
    }
    // Part 2: choose between the live row and the legacy gm-next max once the
    // room's flags are known.
    if (!roomFeaturesReady) return;
    let cancelled = false;
    (async () => {
      try {
        // Part 2 (spec §5): with step authority on, session_live_state is the
        // sole authority for hydration. The max-gm-next query below is only for
        // a session that has no live row at all (legacy).
        if (stepAuthorityRef.current) {
          const found = await liveStateReadRef.current?.('flowsteps-load');
          if (cancelled || found) return;
        }
        const { data: latestEvent } = await supabase
          .from('game_events_v2')
          .select('payload, created_at')
          .eq('session_id', sessionCode)
          .eq('event_type', 'gm-next')
          // Furthest step, NOT newest row: a delayed durable replay (TAS-253)
          // can insert an OLDER step with a NEWER created_at; the game only
          // advances, so max(stepIndex) is the truth. jsonb `->` orders
          // numerically (`->>` would compare text: "10" < "9").
          .order('payload->stepIndex', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        const eventStepIndex = latestEvent?.payload?.stepIndex;
        if (
          typeof eventStepIndex === 'number' &&
          eventStepIndex >= 0 &&
          eventStepIndex < flowSteps.length &&
          // Forward-only ONCE the step is confirmed. While it is still the cache
          // seed, the server's answer wins outright — including downward, which
          // is what stops a client-written cache leaving us ahead (TAS-25).
          (!stepConfirmedRef.current || eventStepIndex > stepIndexRef.current)
        ) {
          debugLog('[GF] Applying latest gm-next after flowSteps load:', {
            eventStepIndex,
            current: stepIndexRef.current,
          });
          setStepIndex(eventStepIndex);
        }
        confirmStep();
      } catch (e) {
        debugWarn('[GF] Failed to re-check latest gm-next:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionCode, flowSteps, confirmStep, roomFeaturesReady]);

  // Sync to latest step on window focus/visibility (fallback for missed broadcasts).
  // Primary sync is now via Broadcast channel - this is only for recovery scenarios
  // like returning to a tab after being away, or after network reconnection.
  useEffect(() => {
    if (!sessionCode || !Array.isArray(flowSteps) || flowSteps.length === 0) {
      return;
    }
    let disposed = false;

    const syncToLatestStep = async (trigger = 'unknown') => {
      if (disposed) return;
      try {
        // Part 2 (spec §5): with step authority on, resync means "re-read
        // session_live_state". The gm-next max is only for a session with no
        // live row.
        if (stepAuthorityRef.current) {
          const found = await liveStateReadRef.current?.(`resync-${trigger}`);
          if (disposed || found) return;
        }
        // TAS-374: no `created_at` lower bound. This query used to be filtered
        // to events newer than the latest one AT MOUNT, which after a remount
        // means "newer than where the room already is" — it matched nothing for
        // the rest of the game, and this resync, the one recovery path a
        // backgrounded phone actually triggers, could never fire.
        const { data: latestEvent } = await supabase
          .from('game_events_v2')
          .select('payload, created_at')
          .eq('session_id', sessionCode)
          .eq('event_type', 'gm-next')
          // Furthest step, NOT newest row: a delayed durable replay (TAS-253)
          // can insert an OLDER step with a NEWER created_at; the game only
          // advances, so max(stepIndex) is the truth. jsonb `->` orders
          // numerically (`->>` would compare text: "10" < "9").
          .order('payload->stepIndex', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (disposed) return;
        const eventStepIndex = latestEvent?.payload?.stepIndex;
        if (
          typeof eventStepIndex === 'number' &&
          eventStepIndex >= 0 &&
          eventStepIndex < flowSteps.length &&
          (!stepConfirmedRef.current || eventStepIndex > stepIndexRef.current)
        ) {
          debugLog('[GF] Syncing to latest step from DB:', {
            eventStepIndex,
            current: stepIndexRef.current,
            trigger,
          });
          setStepIndex(eventStepIndex);
        }
        confirmStep();
      } catch (e) {
        debugWarn('[GF] Failed to sync to latest step:', e);
      }
    };

    // Expose sync function via ref so requestSync() can call it on demand
    syncToLatestStepRef.current = syncToLatestStep;

    // TAS-65: a single tab refocus fires BOTH `focus` and `visibilitychange`,
    // so the old pair of handlers ran two identical gm-next resyncs + two
    // sync-request broadcasts per client per refocus — a ~2N query + N broadcast
    // burst against the same session row when a co-located party all pick their
    // phones up on a round advance, exactly when the backend is already serving
    // the advance. Coalesce them with a leading-edge throttle: the first event
    // resyncs immediately (no added latency) and its partner — plus any rapid
    // re-focus — is suppressed for a short window, so each refocus does at most
    // one gm-next fetch + one sync-request.
    let lastResyncAt = 0;
    // TAS-271 PR-5: the throttle above is PER-CLIENT — 16 phones picked up on
    // the same round advance still fire 16 simultaneous gm-next fetches + 16
    // sync-requests. Add a 0-1.5s jitter AFTER the throttle check (single-
    // flight: a pending jittered resync absorbs later triggers) so co-located
    // wake-ups spread instead of bursting.
    let resyncJitterTimer = null;
    const doResync = (trigger, now) => {
      syncToLatestStep(trigger);
      // Nudge the GM (if still active) to re-broadcast the latest step.
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.send({
          type: 'broadcast',
          event: 'sync-request',
          payload: { playerId: playerStorage.get(sessionCode), timestamp: now },
        });
      }
    };
    const resyncOnRefocus = (trigger) => {
      const now = Date.now();
      if (now - lastResyncAt < 500) return;
      lastResyncAt = now;
      if (!isSyncGatingEnabled()) {
        doResync(trigger, now);
        return;
      }
      if (resyncJitterTimer) return; // a resync is already pending
      resyncJitterTimer = setTimeout(() => {
        resyncJitterTimer = null;
        if (disposed) return;
        doResync(trigger, Date.now());
      }, jitterDelay(1500));
    };

    const onFocus = () => resyncOnRefocus('focus');
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') resyncOnRefocus('visibility');
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    // REMOVED: 10-second polling interval - no longer needed with Broadcast
    // The broadcast channel handles real-time sync, and visibility change handles recovery.
    // This reduces network load significantly (was 4 players × 10s = constant polling)

    return () => {
      disposed = true;
      if (resyncJitterTimer) clearTimeout(resyncJitterTimer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [sessionCode, flowSteps, confirmStep]);

  useEffect(() => {
    if (!sessionCode || !currentPlayerId || resumeLock.current) return;

    async function resume() {
      try {
        // Wait for all required data to be loaded
        if (!Array.isArray(flowSteps) || flowSteps.length === 0) {
          debugWarn('⚠️ Flow steps not loaded yet, will try again');
          return;
        }

        if (!Array.isArray(wineQuestions) || wineQuestions.length === 0) {
          debugWarn('⚠️ Questions not loaded yet, will try again');
          return;
        }

        if (!Array.isArray(wineList) || wineList.length === 0) {
          debugWarn('⚠️ Wine list not loaded yet, will try again');
          return;
        }

        // Part 2: the path (live row vs legacy gm-next) depends on the room's
        // flags; wait for them rather than resume on a guess.
        if (!roomFeaturesReady) return;

        // Set the lock to prevent multiple resumes
        resumeLock.current = true;

        // Part 2 (spec §5 Following the room): with step authority on, the
        // resume step IS session_live_state's step. The read below applies it
        // (moving either way while the pointer is unconfirmed) and confirms.
        // The gm-next max and the answer inference further down are only for
        // a session with no live row.
        if (stepAuthorityRef.current) {
          const liveRow = await liveStateReadRef.current?.('resume');
          if (liveRow) {
            const idx = liveRow.step_index;
            if (Number.isInteger(idx) && idx >= 0 && idx < flowSteps.length) {
              setIsResuming(true);
              setResumedStepIndex(idx);
              setTimeout(() => setIsResuming(false), 0);
            }
            return;
          }
        }

        // First, try to resume using the latest GM event (source of truth for step index).
        // Also check local caches as fallbacks for local-only screens such as thank-you.
        // The thank-you continue action is intentionally per-player, so it may be ahead
        // of the latest shared gm-next event.
        // hasn't committed yet (e.g., user refreshes immediately after the GM advances).
        let cachedStepIndex = -1;
        try {
          const sessionCached = sessionStorage.getItem(`tasted_step_${sessionCode}`);
          const localCached = localStorage.getItem(`tasted_step_${sessionCode}`);
          cachedStepIndex = Math.max(
            sessionCached !== null ? Number(sessionCached) : -1,
            localCached !== null ? Number(localCached) : -1
          );
        } catch { /* storage unavailable */ }

        const { data: latestEvent, error: eventError } = await supabase
          .from('game_events_v2')
          .select('payload, created_at, event_type')
          .eq('session_id', sessionCode)
          .eq('event_type', 'gm-next')
          // Furthest step, NOT newest row: a delayed durable replay (TAS-253)
          // can insert an OLDER step with a NEWER created_at; the game only
          // advances, so max(stepIndex) is the truth (jsonb -> orders numerically).
          .order('payload->stepIndex', { ascending: false })
          .limit(1)
          .maybeSingle();

        const dbStepIndex = (!eventError && latestEvent?.payload)
          ? latestEvent.payload?.stepIndex
          : -1;
        // TAS-25: the gm-next DB event is the AUTHORITATIVE shared step. The
        // `tasted_step_<code>` cache is client-writable (localStorage), so we must
        // NOT let it push the resolved step ahead of the server — otherwise a
        // player could write a later index and jump their client to reveal /
        // answer-key steps before the group. Trust the DB step whenever one
        // exists; fall back to the cache only when there is no gm-next yet
        // (dbStepIndex === -1, a fresh session), where it can't leak a future step.
        const dbStep = typeof dbStepIndex === 'number' ? dbStepIndex : -1;
        // TAS-374: the cache fallback is only reachable when the session has NO
        // gm-next at all, and a room in 'waiting' is one that has not begun —
        // fresh, or reset. A reset wipes game_events_v2 but cannot reach this
        // device's localStorage, so without this check a reset room
        // resumes on the device that last played it, in the middle of the flow
        // it was just reset out of. The fallback's real purpose (an advance
        // whose gm-next row has not committed yet) only exists in a room that
        // has started, so nothing legitimate loses it.
        const roomStarted = sessionStatusRef.current !== 'waiting';
        const resolvedStepIndex = dbStep >= 0
          ? dbStep
          : (roomStarted && Number.isFinite(cachedStepIndex) ? cachedStepIndex : -1);
        if (
          resolvedStepIndex >= 0 &&
          resolvedStepIndex < flowSteps.length &&
          // TAS-374: `>=` only once the step is confirmed. An unconfirmed
          // pointer is the cache seed, and the DB's answer outranks it in both
          // directions.
          (!stepConfirmedRef.current || resolvedStepIndex >= stepIndexRef.current)
        ) {
          debugLog(
            '🔄 Resuming at step index',
            resolvedStepIndex,
            '(db:', dbStepIndex, 'cache:', cachedStepIndex, ')'
          );
          setIsResuming(true);
          setResumedStepIndex(resolvedStepIndex);
          setStepIndex(resolvedStepIndex);
          // Clear resume flag after applying
          setTimeout(() => setIsResuming(false), 0);
          confirmStep();
          return;
        }
        confirmStep();

        // Fallback: Infer resume step from the latest answer by mapping to session_questions_v2
        const { data: latestAnswer } = await supabase
          .from('answers_v2')
          .select('id, question_id, created_at')
          .eq('session_id', sessionCode)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!latestAnswer) {
          debugLog('🆕 No answers found, starting from beginning');
          return;
        }

        // Look up question mapping to get wine_id and round_number for this session
        const mappedQuestion = wineQuestions.find((q) => q.question_id === latestAnswer.question_id);

        if (!mappedQuestion) {
          debugWarn(
            '⚠️ Could not map latest answer to session question; starting from beginning'
          );
          return;
        }

        const { wine_id: resumeWineId, round_number: resumeRoundNumber } =
          mappedQuestion;

        // Find the step index corresponding to the results screen of that round
        const resumeIndex = flowSteps.findIndex(
          (step) =>
            step.type === 'results' &&
            step.wine === resumeWineId &&
            step.index === (resumeRoundNumber ?? 1) - 1
        );

        if (resumeIndex !== -1 && resumeIndex >= stepIndexRef.current) {
          debugLog(
            '🔄 Resuming via latest answer at step index',
            resumeIndex
          );
          setIsResuming(true);
          setResumedStepIndex(resumeIndex);
          setStepIndex(resumeIndex);
          // Clear resume flag after applying
          setTimeout(() => setIsResuming(false), 0);
        } else {
          debugWarn(
            '⚠️ Could not locate matching results step; starting from beginning'
          );
        }
      } catch (error) {
        console.error('❌ Error resuming session:', error);
      }
    }

    resume();
  }, [sessionCode, currentPlayerId, flowSteps, wineQuestions, wineList, confirmStep, roomFeaturesReady]);

  // Reset resume lock when session changes
  useEffect(() => {
    resumeLock.current = false;
  }, [sessionCode]);

  const currentStep = useMemo(
    () => flowSteps?.[stepIndex] || null,
    [flowSteps, stepIndex]
  );

  // Wine index is managed by state now

  const currentQuestions = useMemo(() => {
    if (!currentStep?.wine || !Array.isArray(wineQuestions)) return [];
    return wineQuestions.filter((q) => q.wine_id === currentStep.wine);
  }, [wineQuestions, currentStep]);

  const currentQuestion = useMemo(() => {
    return currentQuestions.find(
      (q) => q.round_number === currentStep?.index + 1
    );
  }, [currentQuestions, currentStep]);

  const questionIndex = useMemo(() => {
    return currentQuestions.findIndex(
      (q) => q.round_number === currentStep?.index + 1
    );
  }, [currentQuestions, currentStep]);

  // TAS-357: the flow is on a question step, the questions query has resolved,
  // and there is no session_questions_v2 row for (wine, round). Consumers show
  // an explicit state instead of the loader. Reported to Sentry once per session.
  const missingQuestion = useMemo(
    () => isQuestionRowMissing({ currentStep, currentQuestion, questionsLoaded }),
    [currentStep, currentQuestion, questionsLoaded]
  );

  useEffect(() => {
    if (!missingQuestion) return;
    reportMissingQuestionOnce({ sessionCode, stepIndex, wineId: currentStep?.wine });
  }, [missingQuestion, sessionCode, stepIndex, currentStep]);

  // resultsIndex is now a state variable that can be manipulated
  // Note: Questions are now loaded in the combined runCatalogLoad effect above

  // ── TAS-178: session_live_state reconcile (flag-gated safety net) ──────────
  // A client that missed a broadcast while its socket was quietly dead can always
  // ask the room's authoritative state. Reads the row on mount / focus / (re)subscribe
  // and subscribes to its UPDATEs; applies FORWARD-ONLY via the same prefetch +
  // setStepIndex path the broadcast handler uses. Flag OFF ⇒ this whole effect no-ops.
  useEffect(() => {
    if (!sessionCode) return undefined;

    let disposed = false;

    // Apply a row FORWARD-ONLY. All guards (monotonicity, bounds, phase match,
    // flowSteps-loaded) live in the pure, unit-tested decideLiveStateApply().
    // `handed` marks a row the server gave us in DIRECT RESPONSE to our own
    // write — an advance_session_v2 return value. That is not a read that might
    // have come from a lagging replica; it is the room's position as of our own
    // request. We present an un-banked version for it so a client that has
    // already recorded this version is not locked out of acting on it. That
    // lockout is what turned one missed advance into a four-minute outage in
    // prod session 5017 (see the rejected-advance call site).
    //
    // Part 2 (H2): `authoritative` marks a row returned by this phone's OWN
    // advance_game_step / restart_question / help-sheet call. It may move the
    // phone backwards within the run. The caller (applyAuthoritative) has
    // already checked N2 — same run, version >= the phone's real version — and
    // this path presents the REAL local version, never handed's -1, so an older
    // row that slipped past can still not rewind anything.
    // `showOverlay: false` keeps the transition overlay down (an advance whose
    // caller has its own indicator).
    const applyLiveState = async (row, { handed = false, authoritative = false, showOverlay = true } = {}) => {
      if (disposed || !row) return;

      // TAS-407: an admin reset the room out from under us. Our player row is
      // gone, so every credential this device holds now names a seat that does
      // not exist — staying put would mean a screen that looks fine until the
      // next action fails. Handled BEFORE decideLiveStateApply, because this is
      // not a step to reconcile to; it is the end of this client's run.
      //
      // A full document load rather than a router navigation: the point is to
      // drop every piece of in-memory state along with the stored identity.
      if (row.phase === 'session-reset') {
        if (displayMode) return; // a TV is not a player; let it sit and re-read
        if (sessionResetHandledRef.current) return;
        const priorPlayerId = playerStorage.get(sessionCode);
        // A device with no identity has nothing to clear and is on its way to
        // Join regardless. This only matters in the window before migration
        // 203's first-join trigger flips the phase back to 'lobby' — but a
        // phone that joined AFTER the reset must never be bounced by a marker
        // that was about the run before it (sweep finding, 2026-09-23).
        if (!priorPlayerId) return;
        sessionResetHandledRef.current = true;
        debugLog('[GF] session-reset — clearing identity and returning to join');
        try {
          if (priorPlayerId) claimTokenStorage.clear(priorPlayerId);
          playerStorage.clear(sessionCode);
          gmSecretStorage.clear(sessionCode);
          localStorage.removeItem('isGm');
          localStorage.removeItem('isResuming');
          localStorage.removeItem('resumedStepIndex');
          localStorage.removeItem(`tasted_step_${sessionCode}`);
          localStorage.removeItem(FORK_CHOICE_KEY(sessionCode));
        } catch { /* private mode / quota — the navigation still matters */ }
        try {
          window.location.replace(`/join/${sessionCode}`);
        } catch { /* jsdom and friends */ }
        return;
      }

      // Part 2: a row from a run this phone has already LEFT is a read that
      // started before a reset and landed after it. Following it would rewind
      // into the dead run and purge the new run's queued answers, so it is
      // dropped. Runs are never reused, so this can't hide a real one.
      if (row.run_id && leftRunsRef.current.has(row.run_id)) {
        debugWarn('[GF] live row from a run this phone already left — ignored');
        return;
      }

      // H4: a GM change is noticed on any row that is not older than what we
      // hold, including rows that move nobody.
      if (
        (row.run_id && row.run_id !== liveStateRunIdRef.current)
        || (typeof row.version === 'number' && row.version >= liveStateVersionRef.current)
      ) {
        noteGmSignal(row);
      }

      const prevRunId = liveStateRunIdRef.current;
      const decision = decideLiveStateApply(row, {
        localRunId: liveStateRunIdRef.current,
        localVersion: handed ? -1 : liveStateVersionRef.current,
        steps: flowStepsRef.current,
        // TAS-374: an unconfirmed pointer is a cache guess, not a position we
        // are entitled to defend against the server. Presenting -1 lets any
        // in-bounds authoritative step apply, in either direction.
        currentStepIndex: stepConfirmedRef.current ? stepIndexRef.current : -1,
        authoritative,
        localAttempts: Object.fromEntries(roundAttemptsRef.current),
      });
      const prevVersion = liveStateVersionRef.current;
      // A run reset makes anything queued from the previous run meaningless —
      // and dangerous, since the queue keeps the FURTHEST step while the new
      // run starts at 0 (Codex).
      if (decision.resetToRun) {
        pendingCatchUpRef.current = null;
        clearTimeout(catchUpTimerRef.current);
        // Part 2 (spec §3/§5 Reset): per-run memory goes with the run.
        roundAttemptsRef.current = new Map();
        if (prevRunId) leftRunsRef.current.add(prevRunId);
        // An in-game reset (a new run replacing one this phone was in, never
        // the 'session-reset' teardown handled above) keeps identity but
        // purges this session's queued answers, ratings and events from the
        // old run, and the step cache that would resume the old flow.
        if (prevRunId && (stepAuthorityRef.current || roomFeaturesRef.current.room_reset_session)) {
          try { requestQueue.purgeSession?.(sessionCode, { keepRunId: decision.runId }); } catch { /* best-effort */ }
          try {
            sessionStorage.removeItem(`tasted_step_${sessionCode}`);
            localStorage.removeItem(`tasted_step_${sessionCode}`);
          } catch { /* storage unavailable */ }
        }
      }
      if (decision.resetToRun || decision.runId !== liveStateRunIdRef.current) {
        runGenerationRef.current += 1;
      }
      // Part 2 (H5): remember each round's attempt from every row that is news
      // to us, and tell listeners when the round on screen was restarted.
      const rowIsNews = decision.resetToRun || decision.version !== prevVersion || authoritative || handed;
      if (rowIsNews && flowStepsRef.current.length > 0) {
        const { roundId, attempt } = liveRowRound(row);
        if (decision.resetToRun || (typeof row.version === 'number' && row.version >= prevVersion)) {
          setLiveRound((prev) => (prev.roundId === roundId && prev.attempt === attempt ? prev : { roundId, attempt }));
        }
        if (roundId) {
          const known = roundAttemptsRef.current.get(roundId);
          if (!Number.isInteger(known) || attempt > known) {
            roundAttemptsRef.current.set(roundId, attempt);
            setAttemptTick((t) => t + 1);
          }
        }
      }
      if (decision.attemptChanged) {
        const { roundId, attempt } = liveRowRound(row);
        debugLog('[GF] question restarted on this step — attempt', attempt);
        try { track('Question Restart Seen', { sessionCode, stepIndex: row.step_index, attempt }); } catch { /* */ }
        for (const listener of [...roundRestartListenersRef.current]) {
          try { listener({ roundId, attempt, stepIndex: row.step_index }); } catch { /* one bad listener can't stop the rest */ }
        }
      }
      const generation = runGenerationRef.current;
      // Nothing to apply: record what we have seen and stop.
      if (decision.applyStepIndex == null) {
        // Never bank a LOWER version within the same run: a handed row (our
        // own RPC's answer, presented at -1) can be older than a row banked
        // while it was in flight, and banking it would make the next advance
        // send a stale expected_version.
        const sameRun = decision.runId === prevRunId;
        bankLiveView(decision.runId, sameRun ? Math.max(prevVersion, decision.version) : decision.version);
        return;
      }
      const idx = decision.applyStepIndex;
      const { runId: decidedRun, version: decidedVersion, resetToRun } = decision;
      debugLog('[GF] reconciling from session_live_state → step', idx);
      // TAS-180: a reconcile that actually moves us is a desync we recovered
      // from — count them, with how far behind we were. Non-PII, never throws.
      try {
        track('State Reconciled', {
          sessionCode,
          fromStep: stepIndexRef.current,
          toStep: idx,
          stepGap: idx - stepIndexRef.current,
          versionGap: typeof prevVersion === 'number' && typeof decision.version === 'number' ? decision.version - prevVersion : null,
          resetToRun: Boolean(resetToRun),
          source: row?.__source ?? 'live_state',
        });
      } catch { /* analytics never breaks reconcile */ }
      if (showOverlay) setIsPrefetchingNext(true);
      let prefetched = true;
      try {
        await prefetchStepAssetsRef.current(flowStepsRef.current[idx]);
      } catch {
        // A failed prefetch must not bank this version. The refs used to move
        // before the await, so one transient failure left them claiming a
        // version whose screen never rendered — and every later read of that
        // version was skipped as already seen, including after the network
        // came back (Codex: the likeliest of these to hurt in a real game).
        prefetched = false;
      }
      if (disposed) return;
      // The run moved on while we loaded: this decision belongs to a game that
      // is over. Dropping it is the whole point of the generation.
      if (runGenerationRef.current !== generation) return;
      if (!prefetched) {
        // Leave the refs untouched so the next read retries this same version
        // instead of stepping over it.
        setIsPrefetchingNext(false);
        return;
      }
      // Re-check after the async prefetch. Bail if a NEWER live-state row was
      // processed meanwhile (our monotonicity view advanced past this decision) —
      // never let a slow older reconcile clobber a newer one.
      if (liveStateRunIdRef.current !== decidedRun || liveStateVersionRef.current !== decidedVersion) {
        // Only bail when something NEWER landed. On the first apply of this
        // decision the refs still hold the previous version, which is expected.
        if (liveStateVersionRef.current > decidedVersion) return;
      }
      bankLiveView(decidedRun, decidedVersion);
      // Within a run, stay forward-only (a legacy broadcast may have advanced us
      // during the prefetch). A run_id epoch reset (resetToRun) is ALLOWED to
      // rewind — that's the whole point, so we skip the forward-only guard for it
      // (TAS-181: a GM session-reset must reach players who are on a higher step).
      // An unconfirmed pointer is likewise not ours to defend (TAS-374), and an
      // authoritative row from our own RPC is the room's position (H2).
      if (stepConfirmedRef.current && !resetToRun && !authoritative && idx <= stepIndexRef.current) return;
      setStepIndex(idx);
      confirmStep();
    };

    // Part 2 (H2, N2): apply a row this phone's own RPC returned. It applies
    // only when it is for this phone's run and at least as new as the version
    // the phone really holds; anything older (a delayed `mismatch` overtaken by
    // a later advance, a row from another run) is dropped. `newRun` is for a
    // row that starts a new run (outcome `new_run`, or a reset): it goes
    // through the handed path, which may move backwards via resetToRun.
    // Resolves true when the row was accepted.
    const applyAuthoritative = async (row, { newRun = false, showOverlay = true } = {}) => {
      if (disposed || !row) return false;
      if (newRun && row.run_id && row.run_id !== liveStateRunIdRef.current) {
        await applyLiveState(row, { handed: true, showOverlay });
        return true;
      }
      if (!row.run_id || row.run_id !== liveStateRunIdRef.current) {
        debugWarn('[GF] authoritative row dropped — not this run');
        return false;
      }
      if (typeof row.version !== 'number' || row.version < liveStateVersionRef.current) {
        debugWarn('[GF] authoritative row dropped — older than our version', {
          row: row.version, local: liveStateVersionRef.current,
        });
        return false;
      }
      await applyLiveState({ ...row, __source: row.__source ?? 'authoritative' }, { authoritative: true, showOverlay });
      return true;
    };
    applyAuthoritativeRef.current = applyAuthoritative;

    // Reads through the freshness guard (retry-on-stale) so a lagging replica read
    // can't quietly hand us state older than we've already applied (TAS-181).
    const readAndReconcile = async (reason = 'read') => {
      try {
        const data = await readLiveStateFresh(sessionCode, {
          minVersion: liveStateVersionRef.current,
          runId: liveStateRunIdRef.current,
        });
        if (data) {
          await applyLiveState({ ...data, __source: reason });
          // TAS-374: a row we could actually evaluate is an authoritative answer
          // about where the room is, even when the decision was "stay put" —
          // so the step pointer stops being a guess. Before flowSteps load we
          // could NOT evaluate it (decideLiveStateApply deliberately no-ops and
          // does not bank the row), so claiming an answer there would be a lie.
          if (!disposed && flowStepsRef.current.length > 0) confirmStep();
        }
        // Part 2: the row (or null when there is none / the read failed), so
        // the hydration paths can tell "the live row decided" from "legacy".
        return data || null;
      } catch (err) {
        // TAS-180: a reconcile READ that fails is the "unrecovered desync"
        // precursor — the subscription / next trigger retries, but count it.
        try { track('Reconcile Failed', { sessionCode, reason, message: String(err?.message || err).slice(0, 80) }); } catch { /* */ }
        return null;
      }
    };
    liveStateReadRef.current = readAndReconcile;
    // TAS-374: the advance path needs to apply a row it was HANDED, without
    // going back to the database for state it is already holding.
    applyLiveStateRef.current = applyLiveState;
    // TAS-415: this effect's recovery signals (focus/visible/resubscribe/poll)
    // only ever re-read session_live_state — the room's position, not the
    // catalogue. A phone that reloaded mid-outage and lost the initial
    // session/questions/wines fetch never got another shot at it, so on any
    // of the same recovery signals, also replay that fetch if it failed.
    const retryInitialLoadIfNeeded = () => {
      if (failedLegsRef.current.size === 0) return;
      // TAS-415 review: retry only the leg(s) that actually failed — a
      // wines-only failure must never re-touch the questions leg (blocker #1).
      runCatalogLoadRef.current?.({
        session: failedLegsRef.current.has('session'),
        questions: failedLegsRef.current.has('questions'),
        wines: failedLegsRef.current.has('wines'),
      });
    };
    // Expose this re-read to the App-level stuck-CTA (ConnectivityBanner renders
    // above this provider, so it recovers via the registry, not a ref).
    const unregisterReconciler = registerReconciler(readAndReconcile);

    // Initial read (seed run_id/version + catch up if we mounted behind).
    readAndReconcile();

    // Live UPDATEs + re-read on every (re)subscribe (recovers state missed while disconnected).
    const dispose = resilientChannel({
      topic: () => `live-state:${sessionCode}:${++liveStateChannelSeq}`,
      meta: { sessionCode, context: 'session-live-state' },
      setup: (channel) => {
        channel.on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'session_live_state', filter: `session_id=eq.${sessionCode}` },
          (payload) => { applyLiveState(payload?.new); }
        );
      },
      onStatus: (status) => {
        if (status === 'SUBSCRIBED') {
          readAndReconcile('resubscribe');
          retryInitialLoadIfNeeded();
        }
      },
    });

    // Catch up when the tab regains focus / becomes visible.
    const onFocus = () => { readAndReconcile('focus'); retryInitialLoadIfNeeded(); };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        readAndReconcile('visible');
        retryInitialLoadIfNeeded();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    // A slow heartbeat, because the socket is not a promise.
    //
    // Prod 4458 (19 Sep): 65 subscription failures and 9 exhausted reconnects
    // across four phones on a weak network. Broadcast and postgres_changes both
    // ride that socket, so when it goes the room stops hearing anything — and
    // the only recovery left was the player happening to background and reopen
    // the app. A table of five with phones in hand never does that. The 10s
    // poll this restores was removed when broadcast landed.
    //
    // 20s + jitter, visible tab only, single-flight: a read on a weak network
    // can outlive the interval that started it, and stacking reads punishes the
    // phone this exists to help (Codex).
    const POLL_MS = 20_000;
    let pollTimer = null;
    let pollInFlight = false;
    const schedulePoll = () => {
      clearTimeout(pollTimer);
      pollTimer = setTimeout(async () => {
        if (disposed) return;
        const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
        if (visible && !pollInFlight) {
          pollInFlight = true;
          const generation = runGenerationRef.current;
          try {
            const result = readAndReconcile('poll');
            await result;
            // A read that started in the previous run has nothing to say about
            // this one. applyLiveState checks the same generation, so this is
            // belt and braces for the window before it is reached.
            if (runGenerationRef.current !== generation) {
              debugLog('[GF] poll response dropped — run moved on');
            } else {
              // TAS-415: the phone that never backgrounds (outage happens with
              // the app already in the foreground) has no focus/visible/
              // resubscribe edge to trigger on — this 20s heartbeat is its
              // only other shot at recovery.
              retryInitialLoadIfNeeded();
            }
          } catch { /* reports its own failures */ }
          pollInFlight = false;
        }
        if (!disposed) schedulePoll();
      }, POLL_MS + jitterDelay(4000));
    };
    schedulePoll();

    return () => {
      disposed = true;
      liveStateReadRef.current = null;
      applyLiveStateRef.current = null;
      applyAuthoritativeRef.current = null;
      clearTimeout(pollTimer);
      pendingCatchUpRef.current = null;
      clearTimeout(catchUpTimerRef.current);
      unregisterReconciler();
      try { dispose?.(); } catch { /* already torn down */ }
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionCode]);

  // TAS-178: when flowSteps finish loading, re-read the live-state row once. An
  // initial read that landed BEFORE steps were available did not apply (and, by
  // design, did not advance our monotonicity view), so this recovers the
  // mount/reload catch-up — the exact case gm-next's mount-snapshot filter misses.
  // No-op when the flag is off (liveStateReadRef is never set).
  useEffect(() => {
    if (flowSteps.length > 0) liveStateReadRef.current?.();
  }, [flowSteps.length]);

  // ── Part 2 (Lane F): the exported step-authority API ───────────────────────

  /**
   * Apply a `live` row returned by this phone's own advance_game_step,
   * restart_question or help-sheet call (H2, N2). Stable identity; always calls
   * the current effect's apply path. Resolves true when the row was accepted.
   * @param {object} row
   * @param {{ newRun?: boolean, showOverlay?: boolean }} [opts]
   */
  const applyAuthoritative = useCallback(
    (row, opts) => Promise.resolve(applyAuthoritativeRef.current?.(row, opts) ?? false),
    []
  );

  /** Subscribe to question restarts: `fn({ roundId, attempt, stepIndex })`. */
  const subscribeRoundRestart = useCallback((fn) => {
    if (typeof fn !== 'function') return () => {};
    roundRestartListenersRef.current.add(fn);
    return () => { roundRestartListenersRef.current.delete(fn); };
  }, []);

  /** The attempt this phone knows for a round (0 when it has seen none). */
  /** True once a live row has told this phone the attempt for this round. */
  const roundAttemptKnown = useCallback(
    (roundId) => Boolean(roundId) && roundAttemptsRef.current.has(roundId),
    []
  );
  const roundAttemptFor = useCallback(
    (roundId) => (roundId ? roundAttemptsRef.current.get(roundId) ?? 0 : 0),
    []
  );

  /**
   * `{ runId, attempt }` to pass to buildSubmitAnswerRpcCall / Persist so the
   * answer goes through submit_round_answer, or null while step authority is
   * off for this session (the legacy submit_game_answer path). Read at call
   * time, so a queued answer carries the run and attempt it was given in.
   */
  const roundAnswerTag = useCallback((roundId) => {
    if (!stepAuthorityRef.current || !liveStateRunIdRef.current) return null;
    return {
      runId: liveStateRunIdRef.current,
      attempt: roundId ? roundAttemptsRef.current.get(roundId) ?? 0 : 0,
    };
  }, []);

  // Part 2 (spec §5 Queue, M1, L1): the request queue reports terminal errors
  // here (re-read the live row; after a P0501 also re-read the flags), drops
  // entries from a run or attempt the room has left before sending them, and
  // waits for a live-row re-read on `online` before resuming. Only while step
  // authority or in-game reset is on for the session: with both off the queue
  // behaves exactly as it did before Part 2.
  const queueHooksOn = Boolean(roomFeatures.room_step_authority || roomFeatures.room_reset_session);
  useEffect(() => {
    if (!sessionCode || !queueHooksOn) return undefined;
    const offTerminal = requestQueue.setTerminalErrorHandler?.((error) => {
      if (error?.code === 'P0501') refreshRoomFeaturesRef.current?.();
      Promise.resolve(liveStateReadRef.current?.('queue-terminal')).catch(() => {});
    });
    const offValidator = requestQueue.setEntryValidator?.((item) => {
      const tag = entryTag(item);
      if (!tag || tag.sessionCode !== sessionCode) return true;
      const run = liveStateRunIdRef.current;
      // Re-review finding 1: a run-tagged entry is not sent while this phone
      // doesn't yet know the room's run (it would pass unchecked). It waits;
      // the effect below re-runs the queue once a run is banked.
      if (tag.runId && !run) return 'wait';
      if (tag.runId && run && tag.runId !== run) return false;
      if (tag.roundId && Number.isInteger(tag.attempt)) {
        const known = roundAttemptsRef.current.get(tag.roundId);
        if (Number.isInteger(known) && tag.attempt < known) return false;
      }
      return true;
    });
    const offGate = requestQueue.setResumeGate?.(
      () => liveStateReadRef.current?.('online')
    );
    return () => {
      offTerminal?.();
      offValidator?.();
      offGate?.();
    };
  }, [sessionCode, queueHooksOn]);
  // A run just became known: let waiting entries be checked and sent.
  useEffect(() => {
    if (liveRunId && queueHooksOn) requestQueue.processQueue?.();
  }, [liveRunId, queueHooksOn]);

  // Part 2 (spec §6 N7/NB5): hand the help sheet (rendered above this provider)
  // the apply path it needs. The registry's unregister is token-scoped, so this
  // instance's cleanup can never clear a newer instance's registration.
  const registerGameFlow = useContext(GameFlowRegistryContext);
  useEffect(() => {
    if (typeof registerGameFlow !== 'function') return undefined;
    const unregister = registerGameFlow({ applyAuthoritative, isHydrating, stepIndex });
    return typeof unregister === 'function' ? unregister : undefined;
  }, [registerGameFlow, applyAuthoritative, isHydrating, stepIndex]);

  // Hold the advance lock for a MINIMUM settle window, not just until the work
  // finishes (see the legacy path's finally for the incident behind it). Only
  // ever releases the lock this call took.
  //
  // When the phone MOVED (a new screen is now under the GM's thumb), hold it a
  // little longer after the move as well. On a slow connection the advance can
  // take longer than the 900ms window, and a GM who thought the first tap
  // missed taps again just as the new screen lands: that second tap used to
  // advance the NEW screen — past a reveal nobody saw, with scores already
  // written and no way back (audit B4). A failed advance releases on the
  // normal window so Retry stays quick.
  const releaseAdvanceLock = useCallback((lockToken, { moved = false } = {}) => {
    const MIN_LOCK_MS = 900;
    const AFTER_MOVE_MS = 600;
    const now = Date.now();
    const releaseAt = Math.max(lockToken + MIN_LOCK_MS, moved ? now + AFTER_MOVE_MS : 0);
    const releaseIfStillOurs = () => {
      if (isAdvancingRef.current === lockToken) isAdvancingRef.current = 0;
    };
    if (releaseAt <= now) releaseIfStillOurs();
    else setTimeout(releaseIfStillOurs, releaseAt - now);
  }, []);

  const advanceToNextStepRef = useRef(null);

  // Part 2 (spec §5, contract §4.2): one advance_game_step call and what to do
  // with each outcome. Returns 'advanced' | 'already' | 'mismatch' | 'new_run'
  // | 'failed', or 'legacy' when the server answered P0501 (feature off) and
  // the caller should take the legacy path instead.
  const advanceViaServer = useCallback(async ({ fromStep, suppressOverlay, source, retry }) => {
    const runId = liveStateRunIdRef.current;
    const expectedVersion = liveStateVersionRef.current;
    const reportFailure = (err) => {
      const code = err?.code || '';
      // The server's own sentence for role and precondition refusals; a plain
      // "couldn't reach" for everything else (network, timeout, a bug).
      const message = (code === '42501' || /^P04/.test(code)) && err?.message
        ? err.message
        : ADVANCE_FAILED_COPY;
      try {
        toast.error(message, {
          id: 'gm-advance-failed',
          action: { label: 'Retry', onClick: () => { Promise.resolve(retry?.()).catch(() => {}); } },
        });
      } catch { /* a toast must never break the advance */ }
      try {
        track('GM Advance Rejected', {
          sessionCode, toStep: fromStep + 1, retry: false,
          message: String(err?.message || code || 'no response').slice(0, 80),
        });
      } catch { /* */ }
    };

    let res;
    try {
      res = await supabase.rpc('advance_game_step', {
        p_session_id: sessionCode,
        p_actor_player_id: currentPlayerId,
        p_claim_token: claimTokenStorage.get(currentPlayerId) || null,
        p_run_id: runId,
        p_from_step: fromStep,
        p_expected_version: expectedVersion,
        p_source: source || 'next',
        p_gm_secret: gmSecretStorage.get(sessionCode) || null,
      });
    } catch (e) {
      res = { data: null, error: e };
    }

    if (res?.error) {
      const err = res.error;
      if (err.code === 'P0501') {
        refreshRoomFeaturesRef.current?.();
        return 'legacy';
      }
      // Not the GM any more (a handover this phone hasn't heard of yet):
      // re-read our own seat so the UI stops offering Next.
      if (err.code === '42501') Promise.resolve(checkGmRef.current?.()).catch(() => {});
      console.error('❌ advance_game_step failed:', err);
      reportFailure(err);
      return 'failed';
    }

    const outcome = res?.data?.outcome;
    const live = res?.data?.live;
    if (!live || !['advanced', 'already', 'mismatch', 'new_run'].includes(outcome)) {
      console.error('❌ advance_game_step returned no usable outcome:', res?.data);
      reportFailure(null);
      return 'failed';
    }
    try { track('GM Advance', { sessionCode, toStep: live.step_index, version: live.version, outcome }); } catch { /* */ }

    if (outcome === 'advanced' || outcome === 'already') {
      // Followers re-read the live row on this signal; it never carries the
      // step itself for them to apply. Sent before the GM's own prefetch so the
      // room isn't held behind one phone's image fetch (see the legacy path).
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current
          .send({
            type: 'broadcast',
            event: 'step-advance',
            payload: { stepIndex: live.step_index, runId: live.run_id, version: live.version, timestamp: Date.now() },
          })
          .then((result) => {
            if (result !== 'ok') debugWarn('[GF] step-advance broadcast not delivered:', result);
          })
          .catch((e) => debugWarn('[GF] step-advance broadcast failed:', e));
      }
      await applyLiveStateRef.current?.(
        { ...live, __source: 'advance' },
        { handed: true, showOverlay: !suppressOverlay }
      );
      // Warm-start hint for a reload, as the legacy path writes it (TAS-374:
      // only ever a prefetch seed, never the step).
      try {
        sessionStorage.setItem(`tasted_step_${sessionCode}`, String(live.step_index));
        localStorage.setItem(`tasted_step_${sessionCode}`, String(live.step_index));
      } catch { /* storage unavailable */ }
      return outcome;
    }
    if (outcome === 'mismatch') {
      debugWarn('[GF] advance refused as a mismatch — following the room', {
        from: fromStep, expectedVersion, room: live.step_index, version: live.version,
      });
      await applyAuthoritativeRef.current?.(
        { ...live, __source: 'advance-mismatch' },
        { showOverlay: !suppressOverlay }
      );
      return 'mismatch';
    }
    // new_run: the room was reset under us.
    await applyAuthoritativeRef.current?.(
      { ...live, __source: 'advance-new-run' },
      { newRun: true, showOverlay: !suppressOverlay }
    );
    return 'new_run';
  }, [sessionCode, currentPlayerId]);

  /**
   * Advance the room one step.
   *
   * Two things make a repeated call SAFE rather than merely blocked, which
   * matters because callers legitimately retry (an inert screen skipping
   * itself past the 900ms tap debounce — session 6519, 2026-08-26):
   *
   *  - `expectedStepIndex` is the step the caller believes it is on. It is
   *    re-checked AFTER the lock is acquired, so an attempt that was queued
   *    behind another advance — or resumed late, after the 8s expiry — cannot
   *    advance a screen the room has already moved past. It returns 'stale'
   *    and does nothing.
   *  - The outcome is RETURNED. This used to return undefined, which is how a
   *    rejected advance passed unnoticed and stranded the host on a blank
   *    screen with no control.
   *
   * Nothing bypasses the lock. A caller that needs to get through retries and
   * reads the answer (Codex review, 2026-08-26).
   *
   * Part 2 (spec §5, H2): while `room_step_authority` is on for the session
   * the advance is ONE awaited advance_game_step call carrying the step this
   * phone is on and the live version it last banked. Its outcomes:
   *  - 'advanced' — the room moved on; the GM's phone follows the returned row.
   *    The server's 'already' (a double tap that landed) is reported as
   *    'advanced' too, unless the caller passes `detailed: true`.
   *  - 'mismatch' — the room was not where this phone thought (another
   *    advance, a restart, a GM change, or the 8827 split). The returned row is
   *    applied authoritatively, which may move this phone BACKWARDS; nothing
   *    advanced. Not 'failed', so a caller doesn't count the round as done.
   *  - 'new_run' — the room was reset; the phone follows the new run.
   *  - 'failed' — an error; a toast shows the server's reason with Retry.
   * Without `detailed: true`, 'already' is reported as 'advanced' and
   * 'mismatch'/'new_run' as 'stale', so existing callers never retry blind
   * after the phone has already followed the room.
   * `source` is sent as p_source: 'next' (default), 'auto' or 'help_sheet'.
   * With the flag off the legacy path below runs verbatim.
   *
   * @returns {Promise<'advanced'|'already'|'mismatch'|'new_run'|'busy'|'not-gm'|'no-steps'|'at-end'|'stale'|'failed'>}
   */
  const advanceToNextStep = useCallback(async ({
    skipOverlay = false,
    expectedStepIndex = null,
    source = 'next',
    detailed = false,
  } = {}) => {
    debugGroup('🚀 GameFlowProvider: advanceToNextStep called');

    // Guard against concurrent calls — prevents double-advance race conditions
    // (e.g., auto-advance timer + manual click, or two useEffects triggering
    // simultaneously). The lock self-expires: an advance wedged on a dying
    // network must not eat every later press for the rest of the session
    // (TAS-168 — GM stranded on wine-reveal, prod session 3827).
    const ADVANCE_LOCK_MS = 8000;
    if (isAdvancingRef.current && Date.now() - isAdvancingRef.current < ADVANCE_LOCK_MS) {
      debugWarn('🚫 Already advancing — ignoring duplicate call');
      debugGroupEnd();
      return 'busy';
    }

    if (!isGm || !currentPlayerId) {
      debugWarn('🚫 Not GM — cannot advance');
      debugGroupEnd();
      return 'not-gm';
    }

    const steps = flowStepsRef.current;
    if (!Array.isArray(steps) || steps.length === 0) {
      debugWarn('🚫 Cannot advance — flowSteps not yet loaded');
      debugGroupEnd();
      return 'no-steps';
    }

    // Use ref for stepIndex to get the freshest value (avoids stale closure)
    const currentIdx = stepIndexRef.current;
    const nextStepIndex = currentIdx + 1;

    debugLog('currentStepIndex (from ref):', currentIdx);
    debugLog('nextStepIndex:', nextStepIndex);

    if (nextStepIndex >= steps.length) {
      debugWarn('🚫 Already at last step — cannot advance');
      debugGroupEnd();
      return 'at-end';
    }

    // Capture ownership AT ACQUISITION. Reading isAdvancingRef in the finally
    // assumed it still held our timestamp — but the lock self-expires after 8s,
    // so a suspended advance that outlives that window would come back and
    // clear a LATER advance's lock, letting a third press overlap or skip a
    // step (codex pair review). The token is immutable and compared, never
    // re-read.
    const lockToken = Date.now();
    isAdvancingRef.current = lockToken;
    let advanced = false;

    // Re-checked HERE, holding the lock, not before it. A caller that waited
    // its turn may be describing a step the room has since left; advancing on
    // its behalf would skip a screen nobody saw.
    if (expectedStepIndex != null && stepIndexRef.current !== expectedStepIndex) {
      debugWarn(`🚫 Stale advance — caller expected step ${expectedStepIndex}, room is on ${stepIndexRef.current}`);
      isAdvancingRef.current = 0;
      debugGroupEnd();
      return 'stale';
    }

    // Part 2 (spec §5): the single step authority. One awaited RPC replaces
    // the gm-next append, its durable replay and the advance_session_v2 dual
    // write. Needs a run id to name; a phone that has never read the live row
    // takes the legacy path (the server trigger keeps that path consistent).
    if (stepAuthorityRef.current && liveStateRunIdRef.current) {
      let outcome = 'failed';
      try {
        localStorage.removeItem('isResuming');
        const nextStep = steps[nextStepIndex];
        const suppressOverlay = skipOverlay || nextStep?.type === 'wine-reveal';
        outcome = await advanceViaServer({
          fromStep: currentIdx,
          suppressOverlay,
          source,
          retry: () => advanceToNextStepRef.current?.({ skipOverlay, source, expectedStepIndex: currentIdx }),
        });
      } catch (error) {
        console.error('❌ Failed to advance step:', error);
        outcome = 'failed';
      }
      if (outcome !== 'legacy') {
        releaseAdvanceLock(lockToken, {
          moved: ['advanced', 'already', 'mismatch', 'new_run'].includes(outcome),
        });
        debugGroupEnd();
        if (detailed) return outcome;
        // Callers that predate Part 2 treat anything but 'advanced'/'stale' as
        // a failure and retry blind (no expectedStepIndex). After a mismatch or
        // a new run the phone has already followed the room, so a blind retry
        // could skip a screen, a restarted question or team setup. To them it
        // is 'stale': the room moved on its own, nothing to retry. Callers that
        // handle the Part 2 outcomes pass `detailed: true`.
        if (outcome === 'already') return 'advanced';
        if (outcome === 'mismatch' || outcome === 'new_run') return 'stale';
        return outcome;
      }
      // P0501: the server says step authority is off for this game after all.
      // The flags are being re-read; this tap takes the legacy path, still
      // holding the lock.
    }

    try {
      // Clear resuming flag once game progresses
      localStorage.removeItem('isResuming');

      const nextStep = steps[nextStepIndex];

      // Show overlay except for wine reveal steps or when caller explicitly suppresses it
      // (e.g., auto-advance already shows its own notification overlay)
      const suppressOverlay = skipOverlay || nextStep?.type === 'wine-reveal';
      setIsPrefetchingNext(!suppressOverlay);

      const committed = await appendGameEvent({
        sessionCode, eventType: 'gm-next', payload: { stepIndex: nextStepIndex }, playerId: currentPlayerId,
      });
      if (committed.error) throw committed.error;

      // Broadcast step advance for instant player sync (~50-100ms), but never
      // gate the GM's own advance on it: send() resolves 'error'/'timed out'
      // WITHOUT throwing when the socket is sick (up to ~10s), which wedged
      // the GM behind this await mid-game (TAS-168, prod 3827). Players who
      // miss the broadcast recover via the gm-next DB insert + fallbacks.
      //
      // Sent BEFORE the GM's own image prefetch. Followers run their own
      // prefetch when the broadcast lands, so awaiting the GM's first only
      // held the whole room behind one phone's image fetch (up to 5 s per
      // image) for no follower benefit. The GM's local step flip still waits
      // for its prefetch below, so its own screen does not arrive uncached.
      if (broadcastChannelRef.current) {
        debugLog('[GF] Broadcasting step advance:', nextStepIndex);
        broadcastChannelRef.current
          .send({
            type: 'broadcast',
            event: 'step-advance',
            payload: { stepIndex: nextStepIndex, timestamp: Date.now() },
          })
          .then((result) => {
            if (result !== 'ok') debugWarn('[GF] step-advance broadcast not delivered:', result);
          })
          .catch((e) => debugWarn('[GF] step-advance broadcast failed:', e));
      }

      await prefetchStepAssets(nextStep);

      // Update local step index immediately (GM sees the change)
      setStepIndex(nextStepIndex);

      // Cache locally so refresh immediately after advancing still finds the right step
      try {
        sessionStorage.setItem(`tasted_step_${sessionCode}`, String(nextStepIndex));
        localStorage.setItem(`tasted_step_${sessionCode}`, String(nextStepIndex));
      } catch { /* storage unavailable */ }

      // Persist to DB for resume/history — still non-blocking (never delays the
      // GM's navigation), but now DURABLE (TAS-253): the raw fire-and-forget
      // insert could be cancelled if the GM's page unloaded right after the tap
      // (refresh/close/phone lock), leaving players who missed the broadcast
      // with no authoritative gm-next row to catch up from. The request queue
      // snapshots to localStorage synchronously on enqueue and replays on the
      // next load/reconnect (TAS-188). Replay is idempotent: a 23505 from the
      // gm-next dedupe index (TAS-156) counts as success — another writer
      // already persisted this step.
      requestQueue.enqueue({
        id: `gm-next:${sessionCode}:${nextStepIndex}`,
        // TAS-360: append_game_event carries the GM's own id + claim token;
        // it returns null (no error) when the dedupe index already holds the
        // row, which the queue's rpc op treats as success.
        persist: {
          ops: [gameEventOp({
            sessionCode,
            eventType: 'gm-next',
            payload: { stepIndex: nextStepIndex },
            playerId: currentPlayerId,
          })],
        },
        priority: 10, // ahead of answer/rating writes — the room is waiting on it
        onSuccess: () => debugLog('[GF] step advance persisted (durable queue)'),
        onError: (err) => console.error('❌ Failed to persist step advance to DB:', err),
      });

      // TAS-178 (flag-gated): dual-write the server-authoritative session_live_state
      // row. Broadcast + gm-next above stay the primary sync; this row is the safety
      // net a STUCK client reconciles from. Non-blocking; CAS on version — we adopt
      // the returned row so a lost race just re-syncs our local (run_id, version).
      if (isLiveStateReconcileEnabled()) {
        // TAS-190: use advance_session_v2 and send the signed GM token. The RPC
        // ignores it while require_gm_secret is off (== the v1 behaviour), and
        // enforces it once the flag flips — so shipping this client is a safe
        // no-op until the flag is enabled. Read the cached secret PER ATTEMPT so a
        // token re-claim (below) takes effect on the retry.
        const writeLiveState = (expectedVersion, isRetry, tokenRetried) => {
          supabase.rpc('advance_session_v2', {
            p_session_id: sessionCode,
            p_expected_version: expectedVersion,
            p_actor_player_id: currentPlayerId,
            p_next_step: nextStepIndex,
            p_phase: nextStep?.type ?? 'unknown',
            p_payload: {
              wine: nextStep?.wine ?? null,
              round: nextStep?.index ?? null,
              hideDetails: !!nextStep?.hideDetails,
            },
            p_gm_secret: gmSecretStorage.get(sessionCode),
          }).then(async ({ data, error: rpcError }) => {
            if (rpcError) {
              // TAS-190: a GM handoff can rotate the secret out from under a
              // re-promoted GM whose device still holds the old one. On a token
              // rejection, re-claim a fresh secret (migration 059 rebinds to the
              // current GM) and retry ONCE.
              if (!tokenRetried && rpcError.message && rpcError.message.includes('GM token')) {
                const fresh = await claimAndStoreGmSecret(sessionCode, currentPlayerId, { force: true });
                if (fresh) { writeLiveState(expectedVersion, isRetry, true); return; }
              }
              debugWarn('[GF] advance_session_v2 RPC failed:', rpcError.message);
              // TAS-180: the authoritative write was REJECTED — the room may now
              // run on broadcast alone. Count it.
              try { track('GM Advance Rejected', { sessionCode, toStep: nextStepIndex, retry: Boolean(isRetry), message: String(rpcError.message || '').slice(0, 80) }); } catch { /* */ }
              return;
            }
            const row = Array.isArray(data) ? data[0] : data;
            if (!row) return;

            // TAS-374. advance_session_v2 only updates `where p_next_step >
            // s.step_index`, and returns the CURRENT row when it doesn't. A row
            // AHEAD of the step we asked for therefore means our advance was
            // rejected because this device is behind the room — and that row is
            // the truth, so we reconcile to it.
            //
            // What used to happen instead is the whole reason prod session 5017
            // lasted four and a half minutes. We banked `row.version` without
            // banking `row.step_index`, which left the client version-current
            // and step-behind. decideLiveStateApply requires version >
            // localVersion, so from that moment every reconcile — the live-state
            // subscription, the focus re-read, the manual recovery CTA — was a
            // silent no-op. The GM's only remaining way forward was to tap
            // Continue until his local index finally passed the room's, which
            // took 48 taps while nine people watched a frozen screen. Hence the
            // reconcile BEFORE the version is adopted, and `handed: true` so the
            // version we may already be holding cannot suppress it.
            if (typeof row.step_index === 'number' && row.step_index > nextStepIndex) {
              debugWarn('[GF] advance rejected — this device is behind the room:', {
                asked: nextStepIndex, room: row.step_index,
              });
              try {
                track('GM Advance Rejected', {
                  sessionCode,
                  toStep: nextStepIndex,
                  roomStep: row.step_index,
                  retry: Boolean(isRetry),
                  message: 'behind-room',
                });
              } catch { /* */ }
              await applyLiveStateRef.current?.(
                { ...row, __source: 'advance-rejected' },
                { handed: true }
              );
              return;
            }

            try { track('GM Advance', { sessionCode, toStep: nextStepIndex, version: row.version, retry: Boolean(isRetry) }); } catch { /* */ }
            bankLiveView(row.run_id, row.version);
            // CAS missed (our version was stale) and the authoritative row is still
            // behind the step we meant to write — retry ONCE with the fresh version
            // so the row doesn't lag the live game (mid-game reload / rapid advance).
            if (!isRetry && typeof row.step_index === 'number' && row.step_index < nextStepIndex) {
              writeLiveState(row.version, true, tokenRetried);
            }
          }).catch((e) => debugWarn('[GF] advance_session_v2 RPC error:', e?.message));
        };
        writeLiveState(liveStateVersionRef.current, false, false);
      }

      debugLog(`🚀 GM advanced to step ${nextStepIndex} successfully`);
      advanced = true;
    } catch (error) {
      // Reported, not rethrown. No caller catches this, so rethrowing produced
      // an unhandled rejection and made the 'failed' outcome below unreachable
      // (Codex review, 2026-08-26).
      console.error('❌ Failed to advance step:', error);
    } finally {
      // Hold the lock for a MINIMUM settle window, not just until the work
      // finishes. The advance completes in ~100-200ms, so releasing here let a
      // second tap 290ms later advance a second time — and because the GM's
      // Continue sits in the same place on consecutive screens, a double tap
      // skipped a whole screen without anyone seeing it. That is how Mel lost
      // the wine reveal in session 6519 (step 17 → 18 in 290ms, verified in
      // game_events_v2).
      //
      // 900ms swallows a double tap while staying well under the 8s
      // self-expiry that protects a wedged advance (TAS-168).
      releaseAdvanceLock(lockToken, { moved: advanced });
      debugLog('🏁 advanceToNextStep function completed');
      debugGroupEnd();
    }
    return advanced ? 'advanced' : 'failed';
  }, [
    isGm,
    currentPlayerId,
    sessionCode,
    prefetchStepAssets,
    advanceViaServer,
    releaseAdvanceLock,
    bankLiveView,
  ]);
  useEffect(() => {
    advanceToNextStepRef.current = advanceToNextStep;
  }, [advanceToNextStep]);

  // Switch identity when sessionCode changes between active sessions.
  //
  // A14 (edge-case audit 2026-09-28): player ids are kept per game now, so the
  // old "clear if storage still holds the previous game's id" check could no
  // longer fire — a GM of game A who opened /lobby?code=B in the same tab kept
  // isGm and A's player id. On any change, load THIS game's id (null when the
  // phone has none; a join that already wrote B's id before navigating —
  // IntroJoin's /join/:code → /onboarding?code=B — is picked up here) and drop
  // GM status until checkGm confirms it for the new game.
  useEffect(() => {
    if (!sessionCode) return;
    const prevCode = prevSessionCodeRef.current;
    if (prevCode && prevCode !== sessionCode) {
      debugLog('🧹 Switching identity for new sessionCode:', sessionCode);
      localStorage.removeItem('isGm');
      localStorage.removeItem('isResuming');
      localStorage.removeItem('resumedStepIndex');
      setIsGm(false);
      setCurrentPlayerId(playerStorage.get(sessionCode));
      // The step pointer is re-seeded and hydration re-entered by the
      // per-room hydration effect above (TAS-374).
    }
    prevSessionCodeRef.current = sessionCode;
  }, [sessionCode]);

  // Perf: Removed context sync debug logging to reduce re-render overhead
  // Note: Wine list is now loaded in the combined runCatalogLoad effect above

  // Update current wine based on flow step
  useEffect(() => {
    if (
      !flowSteps.length ||
      stepIndex >= flowSteps.length ||
      !wineList.length
    ) {
      debugLog('⏭️ Skipping wine update:', {
        hasFlowSteps: flowSteps.length > 0,
        validStepIndex: stepIndex < flowSteps.length,
        hasWines: wineList.length > 0,
      });
      return;
    }

    const currentStep = flowSteps[stepIndex];
    debugGroup('🍷 Wine Update Check');
    debugLog('Current Step:', currentStep);
    debugLog('Current Wine ID:', currentWineId);
    debugLog('Wine List:', wineList);

    // Set current wine ID from step
    if (currentStep.wine) {
      debugLog('🔄 Updating wine to:', currentStep.wine);
      setCurrentWineId(currentStep.wine);
      const newIndex = wineList.indexOf(currentStep.wine);
      if (newIndex !== -1) {
        debugLog('📍 New wine index:', newIndex);
        setWineIndex(newIndex);
      } else {
        debugWarn('⚠️ Wine not found in list:', currentStep.wine);
      }
    }

    // Handle wine details visibility
    if (currentStep.type === 'wine-reveal') {
      debugLog('🎭 Revealing wine details');
      setHideWineDetails(false);
    } else if (currentStep.hideDetails !== undefined) {
      debugLog(
        '🎭 Setting wine details visibility:',
        !currentStep.hideDetails
      );
      setHideWineDetails(currentStep.hideDetails);
    }
    debugGroupEnd();
  }, [stepIndex, flowSteps, wineList]);

  // TAS-415: explicit re-run of the failed leg(s), for a Retry control.
  // Consumers should gate it on `initialLoadError`. Single-flight per leg
  // (see runCatalogLoad) makes a call while one is already in flight a no-op
  // rather than a duplicate request.
  // Game.jsx wiring stays out of scope here (Lane A owns that file) — this
  // is the state/handler it would call.
  const retryInitialLoad = useCallback(() => {
    if (failedLegsRef.current.size === 0) return;
    runCatalogLoadRef.current?.({
      session: failedLegsRef.current.has('session'),
      questions: failedLegsRef.current.has('questions'),
      wines: failedLegsRef.current.has('wines'),
    });
  }, []);

  // Part 2 (H5): the attempt of the round on the current question step.
  const roundAttempt = useMemo(
    () => (currentQuestion?.id ? roundAttemptsRef.current.get(currentQuestion.id) ?? 0 : 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentQuestion, attemptTick]
  );
  const stepAuthority = Boolean(roomFeatures.room_step_authority);

  // The actual value provided to the context
  const contextValue = useMemo(
    () => ({
      // ── Part 2 (Lane F) ──
      // Effective room flags for this session ({room_step_authority, …}).
      roomFeatures,
      refreshRoomFeatures,
      // True while advances go through advance_game_step (flag on).
      stepAuthority,
      // session_live_state run id this phone follows (null until first read).
      runId: liveRunId,
      // Attempt of the current question's round; roundAttemptFor(id) for any.
      roundAttempt,
      // { roundId, attempt } from the latest accepted live row (nulls off a
      // round step). Trust an attempt only when roundId matches your round.
      liveRound,
      roundAttemptFor,
      roundAttemptKnown,
      // { runId, attempt } for submit_round_answer, or null (legacy submit).
      roundAnswerTag,
      // Low-level restart subscription; prefer useRoundRestart(roundId, cb).
      subscribeRoundRestart,
      // Apply a live row returned by this phone's own step/restart RPC (H2, N2).
      applyAuthoritative,
      sessionCode,
      setSessionCode,
      gameType,
      gameMode,
      features,
      gameViewMode,
      tvModeEnabled,
      displayMode,
      sessionPrizes,
      sessionDataLoaded,
      initialLoadError,
      retryInitialLoad,
      setGameType,
      flowSteps,
      setFlowSteps,
      stepIndex,
      setStepIndex,
      stepEnteredAtMs,
      // TAS-374: true while we do not yet know where the room is. Consumers must
      // show a recovery state rather than act on `stepIndex`.
      isHydrating,
      currentPlayerId,
      setCurrentPlayerId,
      isGm,
      setIsGm,
      wineQuestions,
      setWineQuestions,
      currentStep,
      wineList,
      wineTypeMap,
      wineBlendVarietalMap,
      currentWineId,
      setCurrentWineId,
      wineIndex,
      setWineIndex,
      hideWineDetails,
      setHideWineDetails,
      advanceToNextStep,
      currentQuestion,
      questionIndex,
      missingQuestion,
      resultsIndex,
      setResultsIndex,
      resetResultsIndex,
      incrementResultsIndex,
      isResuming,
      setIsResuming,
      resumedStepIndex,
      setResumedStepIndex,
      // Transition state
      isPrefetchingNext,
      prefetchMessage,
      prefetchStepAssets,
      endPrefetch,
      // Screen-ready gate for question entrance animation
      screenReady,
      setScreenReady,
      prefetchProgress,
      // Perf: gmName moved into useMemo to avoid object spread on every render
      gmName,
      // On-demand sync for consumers needing immediate catch-up (e.g., after all players submit)
      requestSync,
    }),
    [
      sessionCode,
      gameType,
      gameMode,
      features,
      gameViewMode,
      tvModeEnabled,
      displayMode,
      sessionPrizes,
      sessionDataLoaded,
      initialLoadError,
      retryInitialLoad,
      flowSteps,
      stepIndex,
      stepEnteredAtMs,
      isHydrating,
      currentPlayerId,
      isGm,
      wineQuestions,
      currentStep,
      wineList,
      wineTypeMap,
      wineBlendVarietalMap,
      currentWineId,
      wineIndex,
      hideWineDetails,
      advanceToNextStep,
      currentQuestion,
      questionIndex,
      missingQuestion,
      resultsIndex,
      resetResultsIndex,
      incrementResultsIndex,
      isResuming,
      resumedStepIndex,
      isPrefetchingNext,
      prefetchMessage,
      prefetchStepAssets,
      endPrefetch,
      prefetchProgress,
      screenReady,
      gmName,
      requestSync,
      roomFeatures,
      refreshRoomFeatures,
      stepAuthority,
      liveRunId,
      roundAttempt,
      liveRound,
      roundAttemptFor,
      roundAttemptKnown,
      roundAnswerTag,
      subscribeRoundRestart,
      applyAuthoritative,
    ]
  );

  return (
    <GameFlowContext.Provider value={contextValue}>
      {children}
    </GameFlowContext.Provider>
  );
}
